import { Box, Typography, Stack, Table, TableBody, TableRow, TableCell } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InsightsIcon from '@mui/icons-material/Insights';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ExtensionIcon from '@mui/icons-material/Extension';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HealingIcon from '@mui/icons-material/Healing';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import type { Lang } from '../i18n/lang.js';

interface Article {
  q: string;
  a: React.ReactNode;
  tags?: string[];
}
interface Group {
  id: string;
  title: string;
  blurb: string;
  icon: React.ReactNode;
  articles: Article[];
}

/**
 * The help centre's articles, in both languages.
 *
 * Kept out of the page component because there are 48 of them twice over, and
 * because the two language trees have to be edited side by side — an article
 * that exists in one and not the other is the failure this file makes visible.
 *
 * Code samples are not translated: they are code.
 */

/* ---------- small presentational helpers ---------- */

/** Inline code chip. */
const C = ({ children }: { children: React.ReactNode }) => (
  <Box component="code" sx={{ px: 0.6, py: 0.1, borderRadius: 1, bgcolor: 'action.hover', fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{children}</Box>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.7 }}>{children}</Typography>
);
const Code = ({ children }: { children: string }) => (
  <Box component="pre" sx={{ p: 2, borderRadius: 2, bgcolor: '#0b0e14', color: '#e6edf3', overflowX: 'auto', fontSize: 13, lineHeight: 1.6, fontFamily: 'ui-monospace, monospace', my: 1 }}>{children}</Box>
);
const Steps = ({ items }: { items: React.ReactNode[] }) => (
  <Stack component="ol" sx={{ m: 0, pl: 2.5 }} spacing={0.75}>
    {items.map((it, i) => (<Box key={i} component="li"><Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{it}</Typography></Box>))}
  </Stack>
);
/** Problem → Cause → Fix, or Term → Definition table. */
const KV = ({ rows, cols }: { rows: [React.ReactNode, React.ReactNode, React.ReactNode?][]; cols?: [string, string, string?] }) => (
  <Table size="small" sx={{ mt: 1, '& td': { verticalAlign: 'top', borderColor: 'divider' } }}>
    {cols && (
      <TableBody>
        <TableRow sx={{ '& td': { color: 'text.disabled', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '2px solid', borderColor: 'divider' } }}>
          {cols.map((c, i) => c != null && <TableCell key={i}>{c}</TableCell>)}
        </TableRow>
      </TableBody>
    )}
    <TableBody>
      {rows.map((r, i) => (
        <TableRow key={i}>
          <TableCell sx={{ fontWeight: 600, width: cols?.[2] ? '24%' : '30%' }}><Typography variant="body2">{r[0]}</Typography></TableCell>
          <TableCell><Typography variant="body2" color="text.secondary">{r[1]}</Typography></TableCell>
          {r[2] != null && <TableCell><Typography variant="body2" color="text.secondary">{r[2]}</Typography></TableCell>}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/* ---------- content ---------- */


const EN: Group[] = [
  {
    id: 'quickstart',
    title: 'Quick start',
    blurb: 'Get sessions recording in about five minutes.',
    icon: <RocketLaunchIcon />,
    articles: [
      {
        q: 'Start recording in 5 minutes',
        tags: ['setup', 'sdk', 'getting started'],
        a: (
          <>
            <P>The fastest path from zero to a first replay:</P>
            <Steps items={[
              <>Create an <b>organization</b> (done for you on a single-tenant install) and sign in, with email/password or <b>Continue with Google</b>.</>,
              <>Go to <b>Admin → API Keys → New project:</b> one project per website or app.</>,
              <>Click the project to open it and copy the pre-filled snippet.</>,
              <>Paste the snippet before <C>&lt;/head&gt;</C> on your site (or use the framework snippet).</>,
              <>Load a page, click around, then open <b>Sessions</b> (or <b>Signals</b>). Your replay appears within seconds.</>,
            ]} />
            <P>No backend changes are required. The SDK captures the DOM, console, network and interaction events entirely from the browser.</P>
          </>
        ),
      },
      {
        q: 'The install snippet',
        tags: ['sdk', 'setup', 'install'],
        a: (
          <>
            <P>The HTML snippet from <b>API Keys</b> is pre-filled with your project id and public ingest key:</P>
            <Code>{`<script src="https://cdn.replayops.com/sdk/v1/replayops.min.js"></script>
<script>
  ReplayOps.init({
    projectId: 'your-project-id',
    apiKey:   'ro_live_…',        // public, safe to embed
    endpoint: 'https://collector.yourhost',
  });
</script>`}</Code>
            <P>Framework snippets (React/Next, Vue/Nuxt, Svelte, Angular) and a cURL smoke-test live on the same page. On a self-hosted install, <C>endpoint</C> points at your own collector rather than the ReplayOps cloud. And when the collector is proxied on the app’s own domain, that is simply <C>https://your-domain</C>.</P>
          </>
        ),
      },
      {
        q: 'Install with npm. React, Next.js, Vue, Angular, Svelte',
        tags: ['sdk', 'install', 'npm', 'react', 'next', 'vue', 'angular', 'svelte'],
        a: (
          <>
            <P>rrweb is a runtime dependency alongside the SDK; the ReplayOps wrapper itself is ~10&nbsp;KB minified (4&nbsp;KB gzipped).</P>
            <Code>{`npm i @replayops/sdk`}</Code>
            <P><b>React / Next.js:</b> the hook starts recording on mount and cleans up on unmount:</P>
            <Code>{`'use client';
import { useReplayOps } from '@replayops/sdk/react';

export function App() {
  useReplayOps({ projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });
  return <YourApp />;
}`}</Code>
            <P><b>Vue / Nuxt</b>:</P>
            <Code>{`import { ReplayOpsPlugin } from '@replayops/sdk/vue';
app.use(ReplayOpsPlugin, { projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });`}</Code>
            <P><b>Angular, Svelte, or anything else:</b> call the singleton once at bootstrap:</P>
            <Code>{`import { Session } from '@replayops/sdk';
Session.init({ projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });`}</Code>
          </>
        ),
      },
      {
        q: 'Mobile apps. Native iOS & Android, and WebViews',
        tags: ['mobile', 'ios', 'android', 'webview', 'hybrid', 'sdk', 'swift', 'kotlin'],
        a: (
          <>
            <P>If your app shows web content in a WebView (Cordova, Ionic, Capacitor, or a native app hosting a web page), the browser SDK runs there unchanged. Load the same snippet in the page the WebView displays; recording, live co-browsing and analytics all work.</P>
            <P>We measured a default iOS <C>WKWebView</C>: storage, DOM recording, the final flush, WebSockets and performance capture all work. Two things need the host app’s cooperation:</P>
            <KV
              cols={['Platform', 'What to set']}
              rows={[
                [<b>Android</b>, <>WebView disables both by default: <C>settings.javaScriptEnabled = true</C> and <C>settings.domStorageEnabled = true</C>. Without DOM storage the anonymous id can’t persist, so a returning user looks like a new one.</>],
                [<b>iOS</b>, <>For tester mode’s report window: <C>preferences.javaScriptCanOpenWindowsAutomatically = true</C> plus a <C>WKUIDelegate</C> that handles <C>createWebViewWith</C>. Without it the SDK shows an in-page notice with a link instead.</>],
              ]}
            />
            <P>Tester mode’s screenshot falls back to a DOM re-render inside a WebView, because the native screen-capture API isn’t available there.</P>
            <P><b>Native screens</b> (UIKit, SwiftUI, Jetpack Compose) are not captured by a WebView integration; they need the native SDKs. Those record what the screen actually looked like (masked and downscaled on the device) plus the touches and screen changes between frames, and replay in this same player.</P>
            <Code>{`// iOS
ReplayOps.start(.init(projectId: "…", apiKey: "ro_live_…", endpoint: "https://replayops.ai"))

// Android
ReplayOps.start(this, ReplayOpsConfig(projectId = "…", apiKey = "ro_live_…", endpoint = "https://replayops.ai"))`}</Code>
            <KV
              cols={['On mobile', 'How it works']}
              rows={[
                ['Frames, not video', 'Capture is change-driven. An idle screen produces nothing, and an unchanged one is never re-sent. No system screen-record permission is involved: only your own app’s window is captured.'],
                ['Masking', 'Typed input is masked by default and password fields always are; other views are masked with ReplayOps.mask(view). Masking happens before the image is encoded, so a masked value never leaves the device.'],
                ['Screens & journeys', 'Screens are detected automatically and become journey steps, so funnels and path analysis work exactly as they do for web sessions.'],
                ['Frustration', 'Taps count as clicks; a tap the app never reacted to is a dead tap, and three in a fingertip’s radius is a rage tap. The same score as the web.'],
                ['Network & crashes', 'HTTP calls (method, URL, status, duration; never bodies or headers) and uncaught crashes are captured too, so they appear in the replay’s Network panel and in the Error Inbox alongside web errors. On iOS network capture is automatic; on Android add the OkHttp interceptor, since the platform has no global hook.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'SDK configuration reference',
        tags: ['sdk', 'config', 'options', 'sampling', 'network'],
        a: (
          <>
            <P>Only <C>projectId</C>, <C>apiKey</C> and <C>endpoint</C> are required. Everything else has a production-sane default.</P>
            <P><b>Privacy:</b> see <b>Privacy &amp; masking</b> below for the full contract.</P>
            <KV
              cols={['Option', 'Default', 'What it does']}
              rows={[
                [<C>maskInputs</C>, 'true', 'Mask every input / textarea / select value.'],
                [<C>maskSelectors</C>, '[]', 'Extra CSS selectors whose subtree text is masked.'],
                [<C>ignoreSelectors</C>, '[]', 'CSS selectors dropped from the recording entirely.'],
                [<C>requireConsent</C>, 'false', 'Record nothing until Session.consent(true).'],
              ]}
            />
            <P><b>Capture channels</b></P>
            <KV
              cols={['Option', 'Default', 'What it does']}
              rows={[
                [<C>recordConsole</C>, 'true', 'Console logs into the DevTools panel.'],
                [<C>recordNetwork</C>, 'true', 'fetch/XHR method, URL, status and timing.'],
                [<C>recordNetworkBodies</C>, 'true', 'Request/response bodies + headers, capped at 8 KB, secrets redacted.'],
                [<C>recordErrors</C>, 'true', 'JS errors and unhandled rejections.'],
                [<C>capturePerformance</C>, 'true', 'Navigation and resource timing.'],
                [<C>captureMemory</C>, 'true', 'JS heap, DOM node count and FPS samples.'],
                [<C>captureWebVitals</C>, 'true', 'LCP / FID / CLS.'],
              ]}
            />
            <P><b>Correlation, live &amp; transport</b></P>
            <KV
              cols={['Option', 'Default', 'What it does']}
              rows={[
                [<C>tracePropagation</C>, 'true', 'Inject W3C traceparent/baggage so replays link to backend APM traces (same-origin only).'],
                [<C>tracePropagationTargets</C>, '[]', 'Extra cross-origin targets (string or RegExp). Those servers must allow the headers via CORS.'],
                [<><C>live</C> + <C>liveChat</C></>, 'true', 'Live visitor list, co-browsing, remote control and the in-page chat widget.'],
                [<C>googleAnalytics</C>, 'true', 'Capture the GA client id so a replay deep-links to the GA session.'],
                [<C>samplingRate</C>, '1', 'Fraction of sessions recorded (0..1). Admin → Who gets recorded sets this per project; when both are set, the stricter of the two applies.'],
                [<C>environment</C>, "'production'", "production | staging | development."],
                [<C>releaseVersion</C>, 'none', 'Ties the session to an uploaded sourcemap for readable stack traces.'],
                [<><C>batchSize</C> / <C>flushIntervalMs</C></>, '100 / 5000', 'Events per batch and max ms between flushes.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'Who gets recorded. Sampling and user / IP lists',
        tags: ['sampling', 'privacy', 'exclude', 'ip', 'users', 'recording'],
        a: (
          <>
            <P>
              Set under <b>Admin → Who gets recorded</b>, per project. Defaults record every
              session, so a project that is left alone behaves as it always did.
            </P>
            <P><b>Sampling</b></P>
            <P>
              Lowering the share reduces storage and the data leaving your site in the same
              proportion. The draw happens in the browser, so the events of an unsampled session
              are never sent at all. Sites loading the SDK from this deployment
              (<C>/sdk/v1/replayops.min.js</C>) get this automatically. Sites installing the npm
              package need <C>@replayops/sdk</C> 0.1.2 or newer; on an older package the project
              setting is ignored and everything is still recorded.
            </P>
            <KV
              cols={['Mode', 'What it means', 'When to pick it']}
              rows={[
                ['Visitor', 'A visitor is either always recorded or never recorded, decided from their anonymous id.', 'Funnels and journey analysis: a sampled person’s whole path is present. A 10% session sample of a five-step funnel shows almost no complete journeys.'],
                ['Session', 'Each visit is decided independently.', 'A fair cross-section of traffic when you do not need any one person followed end to end.'],
              ]}
            />
            <P><b>User and IP lists</b></P>
            <P>
              Each list has three modes: off, record only those listed, or record everyone except
              those listed. User entries match the id you pass to <C>identify()</C> and the email in
              its traits, case-insensitively. IP entries take a single address or a CIDR block such
              as <C>10.0.0.0/8</C>; IPv4 and IPv6 both work.
            </P>
            <P><b>Two consequences worth knowing before you switch a list on</b></P>
            <KV
              cols={['Situation', 'What happens', 'Why']}
              rows={[
                ['“Record only those listed” with an empty list', 'Nothing is recorded for that project.', 'The restriction has been expressed; recording everyone until the first entry is saved would be the opposite of what was asked. The screen warns about this.'],
                ['“Record only those listed”, visitor not identified yet', 'Not recorded, including everything before sign-in.', 'Nobody can say an anonymous visitor is on the list.'],
              ]}
            />
            <P><b>Where the rules are applied</b></P>
            <P>
              At ingest, always. The public key in your snippet is readable by anyone, so a page
              could be edited to ignore whatever it was told. The browser is told the user list as
              hashes rather than values, so the page cannot read who is on it but stops as soon as
              it recognises its own identity there. Because <C>identify()</C> usually runs after the
              first frames are recorded, a rule often becomes applicable mid-session; what was
              already stored for that session is then deleted.
            </P>
          </>
        ),
      },
      {
        q: 'SDK API, identify, track, consent',
        tags: ['sdk', 'api', 'identify', 'track', 'consent'],
        a: (
          <>
            <KV
              cols={['Method', 'Purpose']}
              rows={[
                [<C>Session.init(config)</C>, 'Configure and start (subject to sampling / consent).'],
                [<C>Session.start() / stop()</C>, 'Manual lifecycle control.'],
                [<C>Session.pause() / resume()</C>, 'Suspend and continue recording.'],
                [<C>Session.identify(userId, traits?)</C>, 'Attach an application user. The anonymous id is kept, so earlier anonymous sessions still stitch to the same person.'],
                [<C>Session.track(name, props?)</C>, 'Emit a custom event into the timeline (also usable as a funnel step).'],
                [<C>Session.setUserProperties(props)</C>, 'Merge user traits.'],
                [<C>Session.mask(sel) / ignore(sel)</C>, 'Runtime privacy rules; applied on the next start().'],
                [<C>Session.consent(granted) / optOut()</C>, 'GDPR / KVKK controls.'],
              ]}
            />
            <Code>{`ReplayOps.identify('user_42', { email: 'ada@acme.com', plan: 'pro' });
ReplayOps.track('checkout_completed', { total: 129.9, currency: 'TRY' });`}</Code>
            <P>Identify as early as you know who the visitor is. Sessions, tickets and Most Frustrated Users all key off it.</P>
          </>
        ),
      },
      {
        q: 'What each screen is for (a tour)',
        tags: ['overview', 'navigation'],
        a: (
          <KV
            cols={['Screen', 'Use it to…']}
            rows={[
              [<b>Dashboard</b>, 'See the health of every project at a glance: sessions, frustration, open tickets, live alerts. If you have no projects yet, it prompts you to create your first.'],
              [<b>Live</b>, 'Watch users on your site right now, chat, take control (with consent), and see their live DevTools.'],
              [<b>Sessions</b>, 'Every recorded session, org-wide, filterable by project. Open one to replay it.'],
              [<b>Tickets</b>, 'Work the support queue; every ticket carries its replay and signals.'],
              [<b>Signals</b>, 'Triage the most frustrated sessions across all projects.'],
              [<b>Errors</b>, 'Browse JS / console / network errors grouped by signature.'],
              [<b>Alerts</b>, 'Get notified when frustration, errors or rage spikes.'],
              [<b>AI Summaries</b>, 'Read a plain-language recap of what happened in a session.'],
              [<b>Ask</b>, 'Ask about your recordings in words, and get the answer read out of your own data.'],
              [<b>Analytics</b>, 'Support Analytics, User Behavior (incl. Most Frustrated Users), Conversion Funnel and Heatmaps.'],
              [<b>Admin</b>, 'API Keys (per project), API & MCP access (org-wide), Sourcemaps, Integrations, configuration, security and users.'],
            ]}
          />
        ),
      },
    ],
  },
  {
    id: 'concepts',
    title: 'Core concepts',
    blurb: 'The handful of ideas the whole product is built on.',
    icon: <InsightsIcon />,
    articles: [
      {
        q: 'What is ReplayOps?',
        tags: ['overview'],
        a: (
          <>
            <P>ReplayOps is a session-replay platform with a support brain. It records every user session, automatically scores how frustrated the user was, and turns the worst sessions into support work your team can resolve, in the cloud or fully self-hosted.</P>
            <P>The core loop: <b>capture</b> sessions → <b>score</b> frustration &amp; extract errors → <b>triage</b> via tickets, signals and alerts → <b>resolve</b> with the replay and your issue tracker.</P>
          </>
        ),
      },
      {
        q: 'The frustration score (0–100)',
        tags: ['signals', 'score', 'frustration'],
        a: (
          <>
            <P>Every session gets a 0–100 score computed at ingest from behavioural signals. Higher means more struggle. It powers the <b>Signals</b> ranking, ticket badges, alerts and the User Behavior trend.</P>
            <KV
              cols={['Signal', 'What it means']}
              rows={[
                ['Rage clicks', '≥3 rapid clicks in a small area. The user is hammering something that isn’t responding.'],
                ['Dead clicks', 'A click that produced no DOM reaction. A control that looks clickable but isn’t.'],
                ['Errors', 'JS exceptions, console errors and failed network requests during the session.'],
                ['Bounce / thrash', 'Rapid back-and-forth or an immediate exit after landing.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'Sessions, events & the replay',
        tags: ['sessions', 'replay'],
        a: (
          <>
            <P>A <b>session</b> is one continuous visit. The SDK streams a compact event log (DOM mutations, inputs, clicks, navigation, console and network) to the collector, which stores it in your session store. The <b>replay</b> reconstructs the page from those events. It is a faithful re-render, not a video, so it stays small and text stays searchable.</P>
            <P>Sensitive inputs are masked at capture time and never leave the browser in the clear.</P>
          </>
        ),
      },
      {
        q: 'Projects, organizations & roles',
        tags: ['projects', 'roles', 'users'],
        a: (
          <>
            <P>An <b>organization</b> owns everything. Inside it, each <b>project</b> maps to one site or app and has its own ingest key, retention and analytics.</P>
            <KV
              cols={['Role', 'Can do']}
              rows={[
                [<b>Owner</b>, 'Everything, including deployment configuration and billing.'],
                [<b>Admin</b>, 'Manage projects, API keys, sourcemaps, users, integrations and configuration.'],
                [<b>Member</b>, 'Work tickets, watch replays, view analytics.'],
                [<b>Viewer</b>, 'Read-only access to replays and analytics.'],
              ]}
            />
          </>
        ),
      },
    ],
  },
  {
    id: 'features',
    title: 'Features',
    blurb: 'What each part of the product does.',
    icon: <WidgetsIcon />,
    articles: [
      {
        q: 'Tickets. Turn a session into support work',
        tags: ['tickets'],
        a: (
          <>
            <P>Open <b>Tickets</b> for the support queue. Create a ticket from scratch, or click <b>Create ticket</b> on any replay / frustrated session. The recording, frustration signals and customer are attached automatically.</P>
            <P>Each ticket has a status, priority, assignee, tags and internal notes. Filter by Has Replay / Rage Clicks / JS Errors / High Frustration, and push it to Jira, GitHub or GitLab from the ticket.</P>
          </>
        ),
      },
      {
        q: 'Signals. The most frustrated sessions',
        tags: ['signals'],
        a: <P>The <b>Signals</b> screen ranks sessions across all projects by frustration score, with rage/dead-click and error counts. Watch the replay or spin up a ticket in one click. Use the time-range filter to focus on a window.</P>,
      },
      {
        q: 'Error Inbox, grouped errors',
        tags: ['errors'],
        a: <P>JS, console and network errors are extracted from recordings and grouped by a normalized <b>signature</b> under <b>Errors</b>. Each group shows occurrences, affected sessions, a sample stack trace and jump-to-replay. Mark groups resolved or ignored to keep the inbox clean.</P>,
      },
      {
        q: 'Alerts, get notified proactively',
        tags: ['alerts'],
        a: (
          <>
            <P>Under <b>Alerts</b>, create threshold rules on four metrics: high-frustration sessions, error occurrences, rage-click sessions and new error types. Each rule has a rolling window and a cooldown so you aren’t spammed.</P>
            <P>When a rule fires it lands in the in-app bell and, if configured, notifies Slack or Microsoft Teams. Use <b>Evaluate now</b> to test a rule immediately.</P>
          </>
        ),
      },
      {
        q: 'Live. Co-browsing, control & chat',
        tags: ['live', 'co-browse', 'remote control'],
        a: (
          <>
            <P>The <b>Live</b> screen shows users currently active on your SDK-instrumented sites. No browser plugin needed on their side. Select a visitor to watch their screen in real time, chat with them, and inspect what’s happening.</P>
            <KV
              cols={['Capability', 'How it works']}
              rows={[
                [<b>Co-watch</b>, 'The visitor’s screen streams live as you watch. Reconstructed DOM, not video.'],
                [<b>Take control</b>, <>Click <b>Request control</b>; the visitor sees a consent prompt and must approve. Once granted you can drive their page (cursor, click, scroll and typing) to fill a form or unstick them for them. They can revoke it anytime.</>],
                [<b>Chat</b>, 'A two-way chat widget appears on their page.'],
                [<b>Live DevTools</b>, 'Below the screen, a real-time Network / Console / Errors / Perf panel shows activity since you started watching.'],
              ]}
            />
            <P>Remote control commands are only applied after the visitor consents. The consent rule is enforced server-side.</P>
          </>
        ),
      },
      {
        q: 'Tester mode. File a bug from the page you are testing',
        tags: ['tester', 'bug report', 'qa', 'plan', 'seats'],
        a: (
          <>
            <P>Testers get a 🐞 launcher next to the chat bubble on any page running the SDK. It takes a screenshot of what they are looking at, lets them annotate it (box, arrow, freehand, text) and add a note, then files a ticket. With the replay, the browser/OS, the frustration signals and an AI-suggested title and description already attached. No copy-pasting URLs or “what were you doing?” follow-ups.</P>
            <KV
              cols={['Step', 'What happens']}
              rows={[
                ['1 · Capture', 'The launcher grabs the current screen (the ReplayOps widget hides itself, so it is not in the shot).'],
                ['2 · Annotate', 'Draw on the screenshot and type a note describing what went wrong.'],
                ['3 · File', 'A first-party ReplayOps window opens with the ticket form pre-filled; the tester reviews and submits.'],
                ['4 · Triage', 'The ticket lands in Tickets with the session attached. One click to the replay of the exact moment.'],
              ]}
            />
            <P><b>Who can use it.</b> Only members flagged <b>Tester</b> in <b>Admin → User Management</b>. The check runs server-side, so the launcher is harmless if an ordinary visitor taps it.</P>
            <P><b>It is a plan feature.</b> Each plan decides whether tester mode is included and how many members may hold the flag. User Management shows the count in use and refuses a new grant once the plan’s testers are exhausted; revoking is never blocked, so a downgrade never leaves you stuck. Filing also checks the plan at the moment of use. Flags are kept through a downgrade but stop working until the plan covers them again. See your plan under <b>Admin → Billing</b>.</P>
          </>
        ),
      },
      {
        q: 'AI Summaries',
        tags: ['ai', 'summaries'],
        a: <P><b>AI Summaries</b> generates a plain-language recap of what happened in a session. It uses Claude when an API key is configured, and a deterministic heuristic otherwise. So it still works on air-gapped installs with no outbound calls.</P>,
      },
      {
        q: 'MCP: connecting an assistant to ReplayOps',
        tags: ['mcp', 'claude', 'chatgpt', 'assistant', 'api token'],
        a: (
          <>
            <P>An assistant connected over MCP can answer "why did this customer give up on checkout?" from the recordings themselves. The endpoint is <code>/mcp</code> on this deployment, and it speaks Streamable HTTP.</P>
            <P><b>A connection belongs to an organization, not to a project.</b> A server token carries the organization and a role, and no tool takes a project it has to be given; <code>projectId</code> is only ever an optional filter. So one token reaches every project you can see. Create one under <b>Admin → API & MCP access</b>, and revoke an assistant's access from the same page.</P>
            <P><b>The role is the ceiling.</b> A VIEWER token's assistant reads everything and changes nothing. Writes are refused in the tool with the reason, so the assistant can tell you why rather than appearing to work.</P>
            <P><b>Clients that let you set a header</b> (Claude Desktop, Cursor and the like) use the token directly; the MCP tab on the API Keys page prints a ready configuration. <b>Hosted assistants will not carry a token you hand them.</b> ChatGPT, Slack and Microsoft 365 need this deployment to act as an OAuth authorization server instead. That is off by default; set <code>MCP_OAUTH_ENABLED=true</code> and redeploy, after which the client registers itself and nobody pastes anything.</P>
          </>
        ),
      },
      {
        q: 'Ask: querying your data in words',
        tags: ['ask', 'ai', 'chat', 'mcp'],
        a: (
          <>
            <P><b>Ask</b> answers questions about your recordings. "How many errors did ayse@example.com hit in the last month?" "Which users struggled most this week?" It runs the same read tools our MCP server offers and reads the results back to you. It needs a provider under <b>Admin → AI & Models</b>; without one the page says so instead of guessing.</P>
            <P>Three things are worth knowing. It only reaches <b>your organization’s</b> data, because no tool takes an organization as a parameter. Your own role is the ceiling: a Viewer can ask anything and change nothing, so filing a ticket is refused with the reason. And every answer lists the tools it actually ran, so a measured number and an invented one do not look alike. If the list is empty, treat the answer as unverified.</P>
            <P>Answers can still be wrong. Check the tools under an answer, and open the sessions it names.</P>
          </>
        ),
      },
      {
        q: 'Analytics: Support, User Behavior, Funnel & Heatmaps',
        tags: ['analytics', 'funnel', 'heatmap', 'frustrated users'],
        a: (
          <>
            <P><b>Support Analytics</b> covers ticket volume, resolution time and workload by assignee.</P>
            <P><b>User Behavior</b> shows the frustration-score trend, the <b>Frustration distribution</b> (how sessions spread across score buckets), Click Anomalies, Top Drop-off Points, and <b>Most Frustrated Users:</b> identified visitors ranked by their peak frustration, with session count, rage clicks and account (email domain).</P>
            <P><b>Conversion Funnel</b> measures step-to-step drop-off (by custom events or page paths). <b>Heatmaps</b> shows click density + a scroll map per page; pages are grouped into canonical routes (e.g. <C>/product/:id</C>) by default, and clicking a hot area lists the sessions behind it.</P>
          </>
        ),
      },
      {
        q: 'Inspecting a session, the DevTools panels',
        tags: ['devtools', 'network', 'console', 'errors', 'performance', 'graphql', 'payload'],
        a: (
          <>
            <P>Open any session replay to get browser-style DevTools tabs beside the player:</P>
            <KV
              cols={['Tab', 'Shows']}
              rows={[
                [<b>Console</b>, 'Captured console.{log,info,warn,error} output.'],
                [<b>Network</b>, <>Every fetch/XHR with method, status, timing. Expand a row for the <b>request/response payload + headers</b> and any <b>GraphQL</b> operation. Bodies are size-capped, sensitive JSON keys are redacted and auth/cookie headers masked.</>],
                [<b>Errors</b>, <>JS/console errors with stack traces. <b>Resolve with sourcemap</b> turns a minified stack into original <C>function @ src/file:line</C> (see Sourcemaps).</>],
                [<b>Perf</b>, 'Runtime health sampled through the session. FPS, DOM node count and JS memory.'],
                [<b>Correlate</b>, 'Deep links to the backend APM trace / external replay tool for this session.'],
              ]}
            />
            <P>Payload and performance data are captured by the SDK, so they appear on sessions recorded with a current SDK; older recordings still show the metadata.</P>
          </>
        ),
      },
      {
        q: 'Android crashes. Readable stacks from an obfuscated build',
        tags: ['android', 'crash', 'proguard', 'r8', 'mapping', 'symbolication'],
        a: (
          <>
            <P>An Android release build is obfuscated by R8, so a crash reads <C>at a.c.run(SourceFile:17):</C> useless, and identical-looking across unrelated bugs. Upload the mapping your build already produced and the stack comes back in your own names.</P>
            <Steps items={[
              <>Build a release; the mapping lands at <C>app/build/outputs/mapping/release/mapping.txt</C>.</>,
              <>Open <b>Admin → Sourcemaps</b>, enter the release version your app reports, and upload the file. It’s recognised by name, so there’s nothing to choose.</>,
              <>Open the crash in <b>Errors</b> and resolve the stack.</>,
            ]} />
            <P>Keep <C>-keepattributes SourceFile,LineNumberTable</C> in your ProGuard rules, or the retraced stack has no line numbers. Frames from the framework pass through untouched, and a class the mapping doesn’t know is left alone rather than given an invented name.</P>
            <P><b>iOS</b> needs no upload at all: a crash frame carries the mangled Swift symbol, and resolving the stack turns <C>$s5MyApp14CartControllerC8checkoutyyF</C> into <C>MyApp.CartController.checkout():</C> including whether the crash was inside a closure. File and line numbers, and App Store builds stripped of their symbol table, would need a dSYM upload, which isn’t supported yet.</P>
          </>
        ),
      },
      {
        q: 'Sourcemaps, readable error stacks',
        tags: ['sourcemaps', 'errors', 'stack trace', 'minified'],
        a: (
          <>
            <P>Production JS is minified, so raw stacks point at unreadable locations like <C>main.9f3ac1.js:1:284713</C>. Upload your build’s sourcemaps and ReplayOps resolves stacks to your original source.</P>
            <Steps items={[
              <>Build with sourcemaps on (Vite: <C>build.sourcemap: true</C>). You get <C>.map</C> files next to your bundle.</>,
              <>Open <b>Admin → Sourcemaps</b>, pick the <b>project</b>, enter the <b>release version</b> (it must match what your SDK reports), and upload the <C>.map</C> files.</>,
              <>In a session on that release, open <b>Errors → Resolve with sourcemap</b> to see the original <C>function @ source:line</C>.</>,
            ]} />
            <P>Maps are stored per project + release (gzipped) and only used server-side to symbolicate. They’re never served to the browser.</P>
          </>
        ),
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy & masking',
    blurb: 'Decide what is recorded. Masking happens in the browser, before anything is sent.',
    icon: <VisibilityOffIcon />,
    articles: [
      {
        q: 'How masking works',
        tags: ['privacy', 'masking', 'gdpr', 'kvkk'],
        a: (
          <>
            <P>ReplayOps records the DOM, not video. So masking is not a blur painted over pixels afterwards. Masked text is replaced <b>in the browser, during capture</b>, before the batch is sent. The real value never reaches the collector, the session store, or a replay: there is nothing to leak later and nothing to un-blur.</P>
            <KV
              cols={['Level', 'What happens', 'How to mark it']}
              rows={[
                [<b>Mask</b>, <>The element and its whole subtree are still recorded, but every character of text is replaced (<C>***</C> for page text, <C>•••</C> for input values). Layout and interactions stay intact.</>, <><C>data-mask</C>, <C>data-private</C> or <C>maskSelectors</C></>],
                [<b>Ignore</b>, 'The element is dropped from the recording entirely and replays as an empty placeholder box of the same size.', <><C>data-ignore</C> or <C>ignoreSelectors</C></>],
                [<b>Always-on</b>, 'Passwords, cards, CVV, national IDs and similar fields are masked whether or not you annotate them. This layer cannot be switched off.', 'automatic'],
              ]}
            />
            <P>Rule of thumb: <b>mask</b> when you still want to see the shape of the page, <b>ignore</b> when the element must not be captured at all (a document viewer, a customer photo, a third-party payment iframe).</P>
          </>
        ),
      },
      {
        q: 'Mask a region. Everything between two tags',
        tags: ['privacy', 'masking', 'data-mask', 'data-ignore'],
        a: (
          <>
            <P>Both attributes are inherited by the whole subtree, so you wrap a region once and everything inside it is covered. No per-element annotation.</P>
            <Code>{`<!-- Recorded, but all text inside is masked -->
<section data-mask>
  <h3>Melih Sakarya</h3>
  <p>TR33 0006 1005 1978 6457 8413 26</p>
  <table>…</table>
</section>

<!-- Not recorded at all -->
<div data-ignore>
  <iframe src="https://bank.example/widget"></iframe>
  <img src="/uploads/id-card.jpg" />
</div>`}</Code>
            <P><C>data-private</C> is an alias of <C>data-mask</C>. When you can’t touch the markup (a third-party widget, a design-system component), pass CSS selectors at init instead; they behave exactly like the attributes:</P>
            <Code>{`ReplayOps.init({
  projectId: '…', apiKey: '…', endpoint: '…',
  maskSelectors:   ['.invoice-total', '#patient-notes'],
  ignoreSelectors: ['.stripe-card-frame', 'iframe[src*="bank."]'],
});`}</Code>
            <P>At runtime, <C>Session.mask(selector)</C> and <C>Session.ignore(selector)</C> add rules that apply on the next <C>start()</C>. Already-recorded events are never rewritten. Masking is a capture-time decision.</P>
          </>
        ),
      },
      {
        q: 'Examples: masking a div, a table column, a list, a widget',
        tags: ['privacy', 'masking', 'examples', 'div', 'table', 'selector'],
        a: (
          <>
            <P><b>A whole div and everything in it.</b> Annotate the container once. Nested components and text added later by JavaScript are covered too.</P>
            <Code>{`<div data-mask class="customer-card">
  <h4>Ayşe Yılmaz</h4>
  <span>ayse@example.com</span>
  <span>+90 532 000 00 00</span>
</div>`}</Code>
            <P><b>Only some columns of a table.</b> Put the attribute on the cells, not the table. The order number and total stay readable for support.</P>
            <Code>{`<tr>
  <td>#1042</td>                     <!-- visible -->
  <td data-mask>Ayşe Yılmaz</td>     <!-- masked -->
  <td data-mask>ayse@example.com</td>
  <td>₺1.249,00</td>                 <!-- visible -->
</tr>`}</Code>
            <P>For an entire column a selector is less repetitive (<C>nth-child</C> counts from 1):</P>
            <Code>{`maskSelectors: ['.orders td:nth-child(2)', '.orders td:nth-child(3)']`}</Code>
            <P><b>A list rendered from data.</b> The attribute goes on the repeated element, so every row is covered however long the list grows.</P>
            <Code>{`{patients.map((p) => (
  <li key={p.id} data-mask>{p.fullName}, {p.diagnosis}</li>
))}`}</Code>
            <P><b>Keep the label, mask the value:</b> the replay still shows which field the user struggled with, without the value.</P>
            <Code>{`<div class="field">
  <span class="label">IBAN</span>
  <span class="value" data-mask>TR33 0006 …</span>
</div>`}</Code>
            <P><b>Markup you don’t own</b> (a vendor widget). Target it by selector at init instead:</P>
            <Code>{`maskSelectors: [
  '#intercom-container',
  '.pdf-viewer .page',
  '[data-testid="ssn-field"]',
  'section[aria-label="Medical history"]',
]`}</Code>
            <P><b>Drop it entirely</b> when even the shape is sensitive, or the element is heavy and worthless in a replay:</P>
            <Code>{`<div data-ignore>
  <img src="/uploads/id-card.jpg" />
  <canvas id="signature-pad"></canvas>
  <iframe src="https://bank.example/3d-secure"></iframe>
</div>`}</Code>
            <P><b>At runtime</b>, when sensitivity depends on state:</P>
            <Code>{`if (user.role === 'patient') {
  ReplayOps.mask('.records-panel');
  ReplayOps.stop(); ReplayOps.start();   // rules apply on the next start
}`}</Code>
            <P>Masking always wins for the whole subtree. There is no way to un-mask a child of a masked parent. So structure it as “mask the sensitive containers”, not “mask everything and carve out exceptions”.</P>
          </>
        ),
      },
      {
        q: 'Form inputs, network payloads & the deployment policy',
        tags: ['privacy', 'masking', 'inputs', 'network'],
        a: (
          <>
            <P><C>maskInputs</C> (default on) masks the value of every input, textarea and select. Even with it turned off, a field is still masked when its type is <C>password</C>/<C>email</C>/<C>tel</C>/<C>number</C>/<C>hidden</C>, its <C>autocomplete</C> marks card or password data, or its name/id/aria-label matches <C>password</C>, <C>card</C>, <C>cvv</C>, <C>iban</C>, <C>ssn</C>, <C>tckn</C>, <C>secret</C>, <C>token</C>, <C>pin</C>.</P>
            <P>Captured network bodies are capped at 8&nbsp;KB; JSON keys that look sensitive are replaced with <C>***</C> at any depth, and <C>authorization</C>/<C>cookie</C>/<C>x-api-key</C>-style headers are redacted. Set <C>recordNetworkBodies: false</C> to keep only method, URL, status and timing.</P>
            <P><b>Admin → Security</b> carries a deployment-wide masking policy. The SDK fetches it at init and it <b>overrides</b> what individual sites configure, so you can force masking on everywhere without redeploying anyone’s frontend. If the collector is unreachable the SDK keeps the private default, masking on.</P>
          </>
        ),
      },
      {
        q: 'Consent, retention & the right to erasure (GDPR / KVKK)',
        tags: ['privacy', 'gdpr', 'kvkk', 'consent', 'retention'],
        a: (
          <>
            <P>Set <C>requireConsent: true</C> and the SDK stays idle (no snapshot, no batches) until <C>Session.consent(true)</C>. It is safe to load the snippet before your cookie banner resolves. <C>Session.optOut()</C> stops recording permanently for that browser.</P>
            <P>Each project has a <b>retention window</b> (default 90 days); the retention worker deletes expired sessions and their stored chunks automatically. For a data-subject request, an org admin can erase one application user’s sessions across metadata, object storage and the search index in a single audited action.</P>
          </>
        ),
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    blurb: 'Connect notifiers, issue trackers, APM and other replay tools.',
    icon: <ExtensionIcon />,
    articles: [
      {
        q: 'Connect Slack / Teams / Jira / GitHub …',
        tags: ['integrations'],
        a: (
          <>
            <P>Open <b>Admin → Integrations Hub</b>. Each provider has its own credential fields; use <b>Test</b> (where available) before saving.</P>
            <Steps items={[
              <><b>Alerting &amp; chat</b> (Slack, Teams, Discord, Google Chat, email): paste a webhook URL, used by alerts.</>,
              <><b>Incident &amp; on-call</b> (PagerDuty, Opsgenie): page whoever is on duty. PagerDuty folds repeats of the same alert into one incident instead of a page per firing.</>,
              <><b>Issue tracking</b> (Jira, GitHub, Linear, Trello, Asana, ClickUp, Azure DevOps, GitLab) and <b>customer support</b> (Zendesk, Freshdesk, ServiceNow, Intercom, Help Scout, Salesforce): open a ticket from a session, deep-linked back to the replay, carrying the session facts and a <b>priority mapped from the frustration score</b>. Help desks put the visitor on as requester. <b>Test</b> verifies your credentials before you create anything.</>,
              <><b>APM &amp; tracing</b> (Datadog, New Relic, AppDynamics, Dynatrace, Honeycomb, OpenTelemetry) and <b>logs</b> (Splunk, Kibana, Sumo Logic, Grafana): deep-link a session to the traces and logs behind it.</>,
              <><b>Product analytics</b> (GA, Amplitude, Heap, Pendo, PostHog, Segment, Mixpanel), <b>error monitoring</b> (Sentry, Rollbar, Bugsnag, Crashlytics), <b>flags</b> (LaunchDarkly, Optimizely), <b>voice of customer</b> (Qualtrics, Delighted) and <b>other replay tools</b> (FullStory, LogRocket, Hotjar, Clarity).</>,
            ]} />
            <P>Each card says which of four mechanisms it uses. <b>API</b> (a client we wrote, returns a ticket id), <b>webhook</b> (we POST JSON to a URL you paste), <b>link only</b> (a URL template; nothing leaves ReplayOps and there is no credential to store) or <b>by email</b> (we file a ticket by mailing your desk's intake address). If the tool you want isn't listed but accepts a webhook or has a searchable URL, use the closest webhook / link-only card with your own URL.</P>
          </>
        ),
      },
      {
        q: 'How deep-linking to other tools works',
        tags: ['integrations', 'apm', 'external replay'],
        a: <P>ReplayOps doesn’t copy your data out of other systems. It builds a deep link from identifiers captured in the session (user id, trace id, timestamp) using each provider’s URL template. That keeps setup to a single credential/URL per provider and means the data stays in the source tool.</P>,
      },
    ],
  },
  {
    id: 'onprem',
    title: 'Self-hosted & configuration',
    blurb: 'Run ReplayOps on your own infrastructure, your way.',
    icon: <ShieldIcon />,
    articles: [
      {
        q: 'Run ReplayOps on your own infrastructure',
        tags: ['on-prem', 'air-gapped', 'self-hosted'],
        a: (
          <>
            <P>ReplayOps can run fully on-prem with no AWS dependency. Only Postgres and Redis are required. Use <C>docker-compose.airgapped.yml</C> (see <C>docs/AIRGAPPED.md</C>) for a one-command stack.</P>
            <P>Pick your backends from <b>Admin → Configuration</b>: session storage on S3, MinIO, Ceph or local disk; the ingest queue on SQS or Redis; search on OpenSearch or a Postgres fallback.</P>
          </>
        ),
      },
      {
        q: 'Session storage: S3, MinIO, Ceph or local disk',
        tags: ['storage', 's3', 'minio', 'ceph', 'local'],
        a: (
          <>
            <P>Choose a <b>driver</b> in <b>Configuration → Session storage</b>:</P>
            <KV
              cols={['Driver', 'When to use', 'Key fields']}
              rows={[
                ['Local disk', 'Single-node / air-gapped installs.', <>Just a writable <b>path</b>.</>],
                ['S3 (AWS)', 'AWS-hosted.', <>Bucket + Region; leave <b>Endpoint</b> blank.</>],
                ['S3 (MinIO / Ceph)', 'Self-hosted object store.', <>Pick the <b>Provider preset</b>, set the gateway <b>Endpoint</b> + credentials.</>],
              ]}
            />
            <P><b>Path-style addressing</b> defaults to <C>Auto</C>: virtual-host for real AWS, path-style when an endpoint is set (MinIO/Ceph). Override it only if your gateway needs the opposite.</P>
          </>
        ),
      },
      {
        q: 'Queue & search backends',
        tags: ['queue', 'redis', 'sqs', 'opensearch', 'search'],
        a: (
          <>
            <P><b>Ingest queue</b>: pick <b>Redis</b> (self-hosted, just a URL) or <b>SQS</b> (a queue URL + AWS credentials).</P>
            <P><b>Search</b>: enable <b>OpenSearch</b> (node URL + index) for fast full-text search and funnels, or turn it off to fall back to Postgres. Recording, replay and search all keep working, just with simpler ranking.</P>
          </>
        ),
      },
      {
        q: 'Entering credentials (and how secrets are handled)',
        tags: ['configuration', 'secrets', 'credentials'],
        a: (
          <>
            <P>Enter access keys, secrets and connection URLs directly in <b>Configuration</b>. Secrets are <b>write-only</b>: the API never sends a stored secret back to the browser, the field shows only whether one is set, and leaving it blank <b>keeps the current value</b>. Only the <em>names</em> of changed keys are written to the audit log. Never the values.</P>
          </>
        ),
      },
      {
        q: 'Test connections & applying changes',
        tags: ['configuration', 'test connection', 'restart'],
        a: (
          <>
            <P>Each section on <b>Configuration</b> has a <b>Test connection</b> button that probes the backend with the values you’ve entered. Before you commit them. A blank secret falls back to the running one, so you can test without re-typing it.</P>
            <P>Settings are saved to the database and layered over the environment. Because storage/queue/search are process-level drivers, a saved change shows a <b>restart required</b> banner and takes effect after you restart the API, collector and workers. The save button reads <b>Update configuration</b> once settings are database-managed.</P>
          </>
        ),
      },
      {
        q: 'Security & data residency',
        tags: ['security', 'audit'],
        a: <P>Single-tenant deployments seed one organization and close open registration once the first owner exists. <b>Admin → Security</b> shows the deployment posture, active API keys and an append-only audit log. In air-gapped mode no data leaves your network. Including AI Summaries, which fall back to the local heuristic.</P>,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    blurb: 'Common errors and how to clear them.',
    icon: <HealingIcon />,
    articles: [
      {
        q: 'The storage Test connection fails',
        tags: ['troubleshooting', 's3', 'test connection', '403', 'error'],
        a: (
          <>
            <P>The test reports the exact reason the object store returned. Match the message:</P>
            <KV
              cols={['Message', 'Likely cause', 'Fix']}
              rows={[
                [<>Access denied (HTTP 403)</>, 'The access key/secret can’t see the bucket, or the bucket policy forbids it.', 'Re-enter valid credentials; confirm the key has s3:ListBucket / HeadBucket on it.'],
                [<>Bucket not found (HTTP 404)</>, 'Bucket name or Region is wrong.', 'Check the exact bucket name and that the Region matches where it lives.'],
                [<>Wrong region…</>, 'The bucket lives in a different region than configured.', 'Set Region to the one named in the message.'],
                [<>Hitting real AWS by mistake</>, 'Endpoint is blank while you meant to use MinIO/Ceph/LocalStack.', <>Put the gateway URL in <b>Endpoint</b> (e.g. <C>http://localhost:4566</C> for LocalStack).</>],
              ]}
            />
          </>
        ),
      },
      {
        q: 'A saved change won’t take effect',
        tags: ['troubleshooting', 'restart', 'configuration'],
        a: <P>Storage, queue and search are chosen at process start, so a saved change stays pending until you restart the services (API, collector, workers). The <b>restart required</b> banner clears once the running processes match the saved settings.</P>,
      },
      {
        q: 'No sessions or data are showing',
        tags: ['troubleshooting', 'no data', 'sdk'],
        a: (
          <Steps items={[
            <>Confirm the SDK snippet is on the page and <C>projectId</C> / <C>apiKey</C> match the project you’re viewing.</>,
            <>Check the browser console for a collector error and that <C>endpoint</C> is reachable (CORS / network).</>,
            <>Make sure you’re looking at the right <b>project</b> and time range.</>,
            <>On self-hosted: verify the collector and workers are running and the queue (Redis/SQS) is draining.</>,
          ]} />
        ),
      },
      {
        q: 'Alerts aren’t firing',
        tags: ['troubleshooting', 'alerts'],
        a: <P>Check the rule’s <b>window</b> and <b>cooldown:</b> a recent fire suppresses the next one until the cooldown passes. Use <b>Evaluate now</b> to force a check, and confirm the Slack/Teams webhook is set in Integrations if you expect an external notification.</P>,
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    blurb: 'Quick answers to common questions.',
    icon: <LiveHelpIcon />,
    articles: [
      { q: 'How do users sign in? (Google, forgot password)', a: <P>Sign in with email + password or <b>Continue with Google</b> (shown when Google OAuth is configured. Set <C>GOOGLE_CLIENT_ID</C>/<C>GOOGLE_CLIENT_SECRET</C> and add <C>&lt;app&gt;/v1/auth/google/callback</C> as an authorized redirect URI). Forgot a password? Use <b>Forgot password?</b> on the login screen to get a one-hour reset link by email (needs email/SMTP configured).</P>, tags: ['auth', 'google', 'sso', 'password reset', 'login'] },
      { q: 'Why don’t third-party analytics show as errors?', a: <P>A cross-origin request with status 0 (e.g. a Google Analytics beacon that’s opaque or ad-blocked) is not counted as a network error or friction. Only first-party failures and real 5xx do. This keeps analytics/pixel noise out of your error inbox and frustration score.</P>, tags: ['errors', 'analytics', 'frustration', 'network'] },
      { q: 'Is the ingest API key a secret?', a: <P>No. It’s a public, project-scoped key embedded in your client code so the browser can send events. It can only publish sessions for its project.</P>, tags: ['security', 'api key'] },
      { q: 'How do I add a teammate?', a: <P>Open <b>Admin → User Management</b> and click <b>Add user</b> (owners/admins only). On single-tenant installs users are provisioned here rather than via open sign-up.</P>, tags: ['users'] },
      { q: 'How long are sessions kept?', a: <P>Per-project retention (default 90 days). Older sessions are garbage-collected from Postgres, storage and the search index by the retention worker.</P>, tags: ['retention'] },
      { q: 'Do I need OpenSearch?', a: <P>No. Turn it off in Configuration and search + funnels fall back to Postgres. Recording, replay and search keep working.</P>, tags: ['search', 'opensearch'] },
      { q: 'Does anything leave my network on a self-hosted install?', a: <P>No, when configured for air-gapped mode. Storage, queue and search are all local, and AI Summaries fall back to a built-in heuristic instead of calling out.</P>, tags: ['security', 'air-gapped'] },
      { q: 'How do I open the global search?', a: <P>Press <C>⌘K</C> (macOS) or <C>Ctrl K</C> (Windows/Linux) anywhere to search tickets and sessions.</P>, tags: ['shortcuts', 'search'] },
    ],
  },
  {
    id: 'glossary',
    title: 'Glossary',
    blurb: 'The terms used across the product.',
    icon: <MenuBookIcon />,
    articles: [
      {
        q: 'Terms & definitions',
        tags: ['glossary', 'definitions'],
        a: (
          <KV
            cols={['Term', 'Definition']}
            rows={[
              [<b>Rage click</b>, 'Three or more rapid clicks in a small area. A strong frustration signal.'],
              [<b>Dead click</b>, 'A click that produced no visible DOM reaction.'],
              [<b>Frustration score</b>, 'A 0–100 per-session score summarising struggle signals.'],
              [<b>Signature</b>, 'A normalized fingerprint used to group identical errors together.'],
              [<b>Drop-off point</b>, 'A step in the journey where a notable share of users abandon.'],
              [<b>Single-tenant</b>, 'A deployment with exactly one organization; open sign-up closes after the first owner.'],
              [<b>Path-style addressing</b>, 'S3 URL form (bucket in the path) used by MinIO/Ceph; AWS uses virtual-host.'],
              [<b>Collector</b>, 'The service that receives SDK events and enqueues them for processing.'],
            ]}
          />
        ),
      },
    ],
  },
];

const TR: Group[] = [
  {
    id: 'quickstart',
    title: 'Hızlı başlangıç',
    blurb: 'Yaklaşık beş dakikada ilk kaydınızı alın.',
    icon: <RocketLaunchIcon />,
    articles: [
      {
        q: '5 dakikada kayda başlayın',
        tags: ['kurulum', 'sdk', 'başlangıç'],
        a: (
          <>
            <P>Sıfırdan ilk tekrar oynatmaya en kısa yol:</P>
            <Steps items={[
              <>Bir <b>organizasyon</b> oluşturun (tek kiracılı kurulumda sizin için yapılır) ve giriş yapın: e-posta/parola ile ya da <b>Google ile devam et</b>.</>,
              <><b>Admin → API Anahtarları → Yeni proje</b> adımına gidin. Her web sitesi veya uygulama için bir proje.</>,
              <>Projeyi açmak için üzerine tıklayın ve hazır gelen kod parçasını kopyalayın.</>,
              <>Kod parçasını sitenizde <C>&lt;/head&gt;</C> etiketinden önce yapıştırın (ya da framework kod parçasını kullanın).</>,
              <>Bir sayfa açın, tıklayın, sonra <b>Oturumlar</b> (veya <b>Sinyaller</b>) ekranını açın. Kaydınız saniyeler içinde görünür.</>,
            ]} />
            <P>Hiçbir backend değişikliği gerekmez. SDK; DOM’u, konsolu, ağ trafiğini ve etkileşim olaylarını tamamen tarayıcıdan yakalar.</P>
          </>
        ),
      },
      {
        q: 'Kurulum kod parçası',
        tags: ['sdk', 'kurulum', 'install'],
        a: (
          <>
            <P><b>API Anahtarları</b> ekranındaki HTML kod parçası, proje kimliğiniz ve herkese açık ingest anahtarınızla hazır gelir:</P>
            <Code>{`<script src="https://cdn.replayops.com/sdk/v1/replayops.min.js"></script>
<script>
  ReplayOps.init({
    projectId: 'your-project-id',
    apiKey:   'ro_live_…',        // public, safe to embed
    endpoint: 'https://collector.yourhost',
  });
</script>`}</Code>
            <P>Framework kod parçaları (React/Next, Vue/Nuxt, Svelte, Angular) ve bir cURL duman testi aynı sayfada. Kendi sunucunuzdaki kurulumda <C>endpoint</C>, ReplayOps bulutu yerine kendi collector’ınızı gösterir. Collector uygulamanın kendi alan adı üzerinden proxy’leniyorsa bu yalnızca <C>https://alan-adiniz</C> olur.</P>
          </>
        ),
      },
      {
        q: 'npm ile kurulum. React, Next.js, Vue, Angular, Svelte',
        tags: ['sdk', 'kurulum', 'npm', 'react', 'next', 'vue', 'angular', 'svelte'],
        a: (
          <>
            <P>rrweb, SDK ile birlikte gelen bir çalışma zamanı bağımlılığıdır; ReplayOps sarmalayıcısının kendisi küçültülmüş hâlde ~10&nbsp;KB (gzip ile 4&nbsp;KB).</P>
            <Code>{`npm i @replayops/sdk`}</Code>
            <P><b>React / Next.js:</b> hook, bileşen bağlandığında kaydı başlatır, ayrıldığında temizler:</P>
            <Code>{`'use client';
import { useReplayOps } from '@replayops/sdk/react';

export function App() {
  useReplayOps({ projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });
  return <YourApp />;
}`}</Code>
            <P><b>Vue / Nuxt</b>:</P>
            <Code>{`import { ReplayOpsPlugin } from '@replayops/sdk/vue';
app.use(ReplayOpsPlugin, { projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });`}</Code>
            <P><b>Angular, Svelte veya başka bir şey:</b> açılışta singleton’ı bir kez çağırın:</P>
            <Code>{`import { Session } from '@replayops/sdk';
Session.init({ projectId: 'proj_xxx', apiKey: 'ro_live_xxx', endpoint: 'https://collector.yourhost' });`}</Code>
          </>
        ),
      },
      {
        q: 'Mobil uygulamalar. Yerel iOS & Android ve WebView’ler',
        tags: ['mobil', 'ios', 'android', 'webview', 'hibrit', 'sdk', 'swift', 'kotlin'],
        a: (
          <>
            <P>Uygulamanız web içeriğini bir WebView içinde gösteriyorsa (Cordova, Ionic, Capacitor ya da bir web sayfasını barındıran yerel bir uygulama. Tarayıcı SDK’sı orada da değişmeden çalışır. WebView’in gösterdiği sayfaya aynı kod parçasını yükleyin; kayıt, canlı ortak gezinme ve analitik hepsi çalışır.</P>
            <P>Varsayılan bir iOS <C>WKWebView</C> üzerinde ölçtük: depolama, DOM kaydı, son gönderim, WebSocket’ler ve performans yakalama çalışıyor. İki şey ana uygulamanın iş birliğini gerektiriyor:</P>
            <KV
              cols={['Platform', 'Ne ayarlanmalı']}
              rows={[
                [<b>Android</b>, <>WebView ikisini de varsayılan olarak kapatır: <C>settings.javaScriptEnabled = true</C> ve <C>settings.domStorageEnabled = true</C>. DOM depolama olmadan anonim kimlik kalıcı olamaz, dönen bir kullanıcı yeni biri gibi görünür.</>],
                [<b>iOS</b>, <>Test modunun rapor penceresi için: <C>preferences.javaScriptCanOpenWindowsAutomatically = true</C> ve <C>createWebViewWith</C> çağrısını karşılayan bir <C>WKUIDelegate</C>. Bu olmadan SDK sayfa içinde bağlantılı bir bildirim gösterir.</>],
              ]}
            />
            <P>Test modunun ekran görüntüsü WebView içinde DOM’un yeniden çizimine düşer, çünkü yerel ekran yakalama API’si orada yoktur.</P>
            <P><b>Yerel ekranlar</b> (UIKit, SwiftUI, Jetpack Compose) WebView entegrasyonuyla yakalanmaz; yerel SDK’lar gerekir. Bunlar ekranın gerçekte nasıl göründüğünü (cihazda maskelenmiş ve küçültülmüş olarak), kareler arasındaki dokunuşları ve ekran değişimlerini kaydeder ve aynı oynatıcıda izlenir.</P>
            <Code>{`// iOS
ReplayOps.start(.init(projectId: "…", apiKey: "ro_live_…", endpoint: "https://replayops.ai"))

// Android
ReplayOps.start(this, ReplayOpsConfig(projectId = "…", apiKey = "ro_live_…", endpoint = "https://replayops.ai"))`}</Code>
            <KV
              cols={['Mobilde', 'Nasıl çalışır']}
              rows={[
                ['Video değil, kare', 'Yakalama değişime dayalıdır. Hareketsiz bir ekran hiçbir şey üretmez, değişmemiş bir ekran yeniden gönderilmez. Sistem ekran kaydı izni kullanılmaz: yalnızca kendi uygulamanızın penceresi yakalanır.'],
                ['Maskeleme', 'Yazılan girdiler varsayılan olarak, parola alanları ise her zaman maskelenir; diğer görünümler ReplayOps.mask(view) ile maskelenir. Maskeleme görüntü kodlanmadan önce yapılır, yani maskelenmiş bir değer cihazdan hiç çıkmaz.'],
                ['Ekranlar ve yolculuklar', 'Ekranlar otomatik algılanır ve yolculuk adımlarına dönüşür; huniler ve yol analizi web oturumlarındaki gibi çalışır.'],
                ['Hayal kırıklığı', 'Dokunuşlar tıklama sayılır; uygulamanın tepki vermediği bir dokunuş ölü dokunuştur, parmak ucu yarıçapında üç dokunuş öfke dokunuşudur. Puan web ile aynıdır.'],
                ['Ağ ve çökmeler', 'HTTP çağrıları (metot, URL, durum, süre; gövde ve başlık asla değil) ve yakalanmamış çökmeler de kaydedilir; kaydın Ağ panelinde ve Hata Kutusu’nda web hatalarının yanında görünürler. iOS’ta ağ yakalama otomatiktir; Android’de platformda genel bir kanca olmadığı için OkHttp interceptor’ını ekleyin.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'SDK yapılandırma referansı',
        tags: ['sdk', 'yapılandırma', 'seçenekler', 'örnekleme', 'ağ'],
        a: (
          <>
            <P>Yalnızca <C>projectId</C>, <C>apiKey</C> ve <C>endpoint</C> zorunludur. Geri kalan her şeyin üretime uygun bir varsayılanı vardır.</P>
            <P><b>Gizlilik:</b> tam sözleşme için aşağıdaki <b>Gizlilik ve maskeleme</b> bölümüne bakın.</P>
            <KV
              cols={['Seçenek', 'Varsayılan', 'Ne yapar']}
              rows={[
                [<C>maskInputs</C>, 'true', 'Her input / textarea / select değerini maskeler.'],
                [<C>maskSelectors</C>, '[]', 'Alt ağacındaki metni maskelenecek ek CSS seçiciler.'],
                [<C>ignoreSelectors</C>, '[]', 'Kayıttan tamamen çıkarılan CSS seçiciler.'],
                [<C>requireConsent</C>, 'false', 'Session.consent(true) çağrılana kadar hiçbir şey kaydetmez.'],
              ]}
            />
            <P><b>Yakalama kanalları</b></P>
            <KV
              cols={['Seçenek', 'Varsayılan', 'Ne yapar']}
              rows={[
                [<C>recordConsole</C>, 'true', 'Konsol kayıtlarını DevTools paneline taşır.'],
                [<C>recordNetwork</C>, 'true', 'fetch/XHR metodu, URL’si, durumu ve süresi.'],
                [<C>recordNetworkBodies</C>, 'true', 'İstek/yanıt gövdeleri + başlıklar. 8 KB ile sınırlı, sırlar maskelenir.'],
                [<C>recordErrors</C>, 'true', 'JS hataları ve karşılanmamış promise reddi.'],
                [<C>capturePerformance</C>, 'true', 'Navigation ve resource timing.'],
                [<C>captureMemory</C>, 'true', 'JS yığını, DOM düğüm sayısı ve FPS örnekleri.'],
                [<C>captureWebVitals</C>, 'true', 'LCP / FID / CLS.'],
              ]}
            />
            <P><b>İlişkilendirme, canlı ve taşıma</b></P>
            <KV
              cols={['Seçenek', 'Varsayılan', 'Ne yapar']}
              rows={[
                [<C>tracePropagation</C>, 'true', 'W3C traceparent/baggage enjekte eder; kayıtlar backend APM izlerine bağlanır (yalnızca aynı köken).'],
                [<C>tracePropagationTargets</C>, '[]', 'Ek çapraz köken hedefleri (metin ya da RegExp). O sunucular başlıklara CORS ile izin vermelidir.'],
                [<><C>live</C> + <C>liveChat</C></>, 'true', 'Canlı ziyaretçi listesi, ortak gezinme, uzaktan kontrol ve sayfa içi sohbet aracı.'],
                [<C>googleAnalytics</C>, 'true', 'GA istemci kimliğini yakalar; kayıt GA oturumuna doğrudan bağlanır.'],
                [<C>samplingRate</C>, '1', 'Kaydedilen oturumların oranı (0..1). Yönetim → Kimler kaydedilsin ekranı bunu proje bazında belirler; ikisi de ayarlıysa daha kısıtlayıcı olan geçerlidir.'],
                [<C>environment</C>, "'production'", 'production | staging | development.'],
                [<C>releaseVersion</C>, 'yok', 'Oturumu yüklenmiş bir sourcemap’e bağlar; yığın izleri okunur hâle gelir.'],
                [<><C>batchSize</C> / <C>flushIntervalMs</C></>, '100 / 5000', 'Parti başına olay sayısı ve gönderimler arası azami milisaniye.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'Kimler kaydedilsin. Örnekleme ve kullanıcı / IP listeleri',
        tags: ['örnekleme', 'gizlilik', 'hariç tutma', 'ip', 'kullanıcı', 'kayıt'],
        a: (
          <>
            <P>
              <b>Yönetim → Kimler kaydedilsin</b> ekranından, proje bazında ayarlanır.
              Varsayılanlar her oturumu kaydeder; dokunulmayan bir proje eskisi gibi davranır.
            </P>
            <P><b>Örnekleme</b></P>
            <P>
              Oranı düşürmek depolamayı ve sitenizden çıkan veriyi aynı oranda azaltır. Karar
              tarayıcıda verildiği için örnekleme dışında kalan oturumun olayları hiç gönderilmez.
              SDK’yı bu kurulumdan yükleyen siteler (<C>/sdk/v1/replayops.min.js</C>) bunu
              kendiliğinden alır. npm paketini kuran siteler için <C>@replayops/sdk</C> 0.1.2 veya
              üstü gerekir; daha eski bir pakette proje ayarı dikkate alınmaz ve her şey
              kaydedilmeye devam eder.
            </P>
            <KV
              cols={['Mod', 'Ne demek', 'Ne zaman seçilir']}
              rows={[
                ['Ziyaretçi', 'Bir ziyaretçi ya hep kaydedilir ya hiç kaydedilmez; karar anonim kimliğinden türetilir.', 'Huni ve yolculuk analizi: örnekleme içindeki kişinin tüm yolu elde olur. Beş adımlı bir hunide %10’luk oturum örneklemi neredeyse hiç tam yolculuk göstermez.'],
                ['Oturum', 'Her ziyaret kendi başına karara bağlanır.', 'Tek bir kişiyi baştan sona izlemeniz gerekmiyorsa trafiğin adil bir kesiti.'],
              ]}
            />
            <P><b>Kullanıcı ve IP listeleri</b></P>
            <P>
              Her listenin üç modu var: kapalı, yalnızca listedekileri kaydet, listedekiler hariç
              herkesi kaydet. Kullanıcı kayıtları <C>identify()</C> ile verdiğiniz kimlik ve
              trait’lerdeki e-posta ile eşleşir; büyük/küçük harf farkı gözetilmez. IP kayıtları tek
              bir adres ya da <C>10.0.0.0/8</C> gibi bir CIDR bloğu alır; IPv4 ve IPv6 çalışır.
            </P>
            <P><b>Bir listeyi açmadan önce bilmeniz gereken iki sonuç</b></P>
            <KV
              cols={['Durum', 'Ne olur', 'Neden']}
              rows={[
                ['“Yalnızca listedekileri kaydet” ve liste boş', 'O projede hiçbir şey kaydedilmez.', 'Kısıtlama zaten ifade edilmiştir; ilk kayıt eklenene kadar herkesi kaydetmek istenenin tersi olurdu. Ekran bu durumu uyarı olarak gösterir.'],
                ['“Yalnızca listedekileri kaydet” ve ziyaretçi henüz tanımlanmamış', 'Kaydedilmez, girişten önceki her şey dahil.', 'Anonim bir ziyaretçinin listede olduğu söylenemez.'],
              ]}
            />
            <P><b>Kurallar nerede uygulanır</b></P>
            <P>
              Her zaman sunucuya veri ulaştığında. Snippet’inizdeki public anahtar herkes
              tarafından okunabilir, yani sayfa kendisine söyleneni yok sayacak şekilde
              değiştirilebilir. Tarayıcıya kullanıcı listesi değerler değil hash’ler olarak
              bildirilir; sayfa listede kimin olduğunu okuyamaz ama kendi kimliğini tanıdığı anda
              durur. <C>identify()</C> genelde ilk kareler kaydedildikten sonra çalıştığı için kural
              çoğu zaman oturum ortasında uygulanabilir hale gelir; o oturum için saklanmış olan
              veri bu durumda silinir.
            </P>
          </>
        ),
      },
      {
        q: 'SDK API, identify, track, consent',
        tags: ['sdk', 'api', 'identify', 'track', 'onay'],
        a: (
          <>
            <KV
              cols={['Metot', 'Amacı']}
              rows={[
                [<C>Session.init(config)</C>, 'Yapılandırır ve başlatır (örnekleme / onaya tabi).'],
                [<C>Session.start() / stop()</C>, 'Yaşam döngüsünü elle yönetir.'],
                [<C>Session.pause() / resume()</C>, 'Kaydı askıya alır ve sürdürür.'],
                [<C>Session.identify(userId, traits?)</C>, 'Bir uygulama kullanıcısı bağlar. Anonim kimlik korunur, böylece önceki anonim oturumlar da aynı kişiye eklenir.'],
                [<C>Session.track(name, props?)</C>, 'Zaman çizelgesine özel bir olay ekler (huni adımı olarak da kullanılabilir).'],
                [<C>Session.setUserProperties(props)</C>, 'Kullanıcı özniteliklerini birleştirir.'],
                [<C>Session.mask(sel) / ignore(sel)</C>, 'Çalışma zamanı gizlilik kuralları; bir sonraki start() ile geçerli olur.'],
                [<C>Session.consent(granted) / optOut()</C>, 'GDPR / KVKK denetimleri.'],
              ]}
            />
            <Code>{`ReplayOps.identify('user_42', { email: 'ada@acme.com', plan: 'pro' });
ReplayOps.track('checkout_completed', { total: 129.9, currency: 'TRY' });`}</Code>
            <P>Ziyaretçinin kim olduğunu öğrendiğiniz anda identify çağırın. Oturumlar, talepler ve En Çok Zorlanan Kullanıcılar hep buna dayanır.</P>
          </>
        ),
      },
      {
        q: 'Hangi ekran ne işe yarar (kısa tur)',
        tags: ['genel bakış', 'gezinme'],
        a: (
          <KV
            cols={['Ekran', 'Ne için kullanılır']}
            rows={[
              [<b>Panel</b>, 'Her projenin sağlığını tek bakışta görün: oturumlar, hayal kırıklığı, açık talepler, canlı uyarılar. Henüz projeniz yoksa ilkini oluşturmanızı ister.'],
              [<b>Canlı</b>, 'Şu anda sitenizdeki kullanıcıları izleyin, sohbet edin, (onayla) kontrolü devralın ve canlı DevTools’larını görün.'],
              [<b>Oturumlar</b>, 'Organizasyon genelindeki tüm kayıtlar, projeye göre süzülebilir, açıp izleyin.'],
              [<b>Talepler</b>, 'Destek kuyruğunu işleyin; her talep kendi kaydını ve sinyallerini taşır.'],
              [<b>Sinyaller</b>, 'Tüm projelerdeki en çok zorlanılan oturumları önceliklendirin.'],
              [<b>Hatalar</b>, 'JS / konsol / ağ hatalarına imzalarına göre gruplanmış olarak göz atın.'],
              [<b>Uyarılar</b>, 'Hayal kırıklığı, hata ya da öfke tıklaması arttığında haber alın.'],
              [<b>AI Özetleri</b>, 'Bir oturumda ne olduğunu sade bir dille okuyun.'],
              [<b>Sor</b>, 'Kayıtlarınızı kendi cümlelerinizle sorun; yanıt kendi verinizden okunur.'],
              [<b>Analitik</b>, 'Destek Analitiği, Kullanıcı Davranışı (En Çok Zorlanan Kullanıcılar dahil), Dönüşüm Hunisi ve Isı Haritaları.'],
              [<b>Admin</b>, 'API Anahtarları (proje başına), API ve MCP erişimi (şirket geneli), Sourcemap’ler, Entegrasyonlar, yapılandırma, güvenlik ve kullanıcılar.'],
            ]}
          />
        ),
      },
    ],
  },
  {
    id: 'concepts',
    title: 'Temel kavramlar',
    blurb: 'Ürünün tamamının üzerine kurulduğu birkaç fikir.',
    icon: <InsightsIcon />,
    articles: [
      {
        q: 'ReplayOps nedir?',
        tags: ['genel bakış'],
        a: (
          <>
            <P>ReplayOps, destek zekâsı olan bir oturum tekrar oynatma platformudur. Her kullanıcı oturumunu kaydeder, kullanıcının ne kadar zorlandığını otomatik puanlar ve en kötü oturumları ekibinizin çözebileceği destek işine dönüştürür. Bulutta ya da tamamen kendi sunucunuzda çalışır.</P>
            <P>Temel döngü: oturumları <b>yakala</b> → hayal kırıklığını <b>puanla</b> ve hataları çıkar → talepler, sinyaller ve uyarılarla <b>önceliklendir</b> → kayıt ve iş takip aracınızla <b>çöz</b>.</P>
          </>
        ),
      },
      {
        q: 'Hayal kırıklığı puanı (0–100)',
        tags: ['sinyaller', 'puan', 'hayal kırıklığı'],
        a: (
          <>
            <P>Her oturum, davranış sinyallerinden ingest sırasında hesaplanan 0–100 arası bir puan alır. Yüksek olan daha çok zorlanma demektir. <b>Sinyaller</b> sıralamasını, talep rozetlerini, uyarıları ve Kullanıcı Davranışı eğilimini bu puan besler.</P>
            <KV
              cols={['Sinyal', 'Ne anlama gelir']}
              rows={[
                ['Öfke tıklamaları', 'Küçük bir alanda ≥3 hızlı tıklama. Kullanıcı yanıt vermeyen bir şeye yükleniyor.'],
                ['Ölü tıklamalar', 'DOM’da hiçbir tepki üretmeyen tıklama. Tıklanabilir görünen ama olmayan bir öğe.'],
                ['Hatalar', 'Oturum boyunca oluşan JS istisnaları, konsol hataları ve başarısız ağ istekleri.'],
                ['Sekme / savrulma', 'Hızlı gidiş gelişler ya da açılır açılmaz çıkış.'],
              ]}
            />
          </>
        ),
      },
      {
        q: 'Oturumlar, olaylar ve tekrar oynatma',
        tags: ['oturumlar', 'kayıt'],
        a: (
          <>
            <P><b>Oturum</b>, kesintisiz tek bir ziyarettir. SDK; derli toplu bir olay günlüğünü (DOM değişiklikleri, girdiler, tıklamalar, gezinme, konsol ve ağ) collector’a akıtır, o da oturum deponuza yazar. <b>Tekrar oynatma</b> sayfayı bu olaylardan yeniden kurar. Video değil, birebir yeniden çizimdir; bu yüzden küçük kalır ve metin aranabilir olur.</P>
            <P>Hassas girdiler yakalama anında maskelenir ve tarayıcıdan hiçbir zaman açık hâlde çıkmaz.</P>
          </>
        ),
      },
      {
        q: 'Projeler, organizasyonlar ve roller',
        tags: ['projeler', 'roller', 'kullanıcılar'],
        a: (
          <>
            <P>Her şeyin sahibi <b>organizasyondur</b>. İçinde her <b>proje</b> bir siteye ya da uygulamaya karşılık gelir; kendi ingest anahtarı, saklama süresi ve analitiği vardır.</P>
            <KV
              cols={['Rol', 'Yapabildikleri']}
              rows={[
                [<b>Owner</b>, 'Dağıtım yapılandırması ve faturalama dahil her şey.'],
                [<b>Admin</b>, 'Projeleri, API anahtarlarını, sourcemap’leri, kullanıcıları, entegrasyonları ve yapılandırmayı yönetir.'],
                [<b>Member</b>, 'Talepleri işler, kayıtları izler, analitiği görür.'],
                [<b>Viewer</b>, 'Kayıtlara ve analitiğe salt okunur erişim.'],
              ]}
            />
          </>
        ),
      },
    ],
  },
  {
    id: 'features',
    title: 'Özellikler',
    blurb: 'Ürünün her parçası ne yapar.',
    icon: <WidgetsIcon />,
    articles: [
      {
        q: 'Talepler. Bir oturumu destek işine dönüştürün',
        tags: ['talepler'],
        a: (
          <>
            <P>Destek kuyruğu için <b>Talepler</b> ekranını açın. Sıfırdan bir talep oluşturun ya da herhangi bir kayıtta / zorlanılan oturumda <b>Talep oluştur</b>’a tıklayın. Kayıt, hayal kırıklığı sinyalleri ve müşteri otomatik olarak eklenir.</P>
            <P>Her talebin durumu, önceliği, atanan kişisi, etiketleri ve dahili notları vardır. Kayıt Var / Öfke Tıklaması / JS Hatası / Yüksek Hayal Kırıklığı filtreleyin ve talebi oradan Jira, GitHub ya da GitLab’a gönderin.</P>
          </>
        ),
      },
      {
        q: 'Sinyaller. En çok zorlanılan oturumlar',
        tags: ['sinyaller'],
        a: <P><b>Sinyaller</b> ekranı tüm projelerdeki oturumları hayal kırıklığı puanına göre, öfke/ölü tıklama ve hata sayılarıyla birlikte sıralar. Kaydı izleyin ya da tek tıkla talep açın. Belirli bir aralığa odaklanmak için zaman filtresini kullanın.</P>,
      },
      {
        q: 'Hata Kutusu, gruplanmış hatalar',
        tags: ['hatalar'],
        a: <P>JS, konsol ve ağ hataları kayıtlardan çıkarılır ve <b>Hatalar</b> altında normalleştirilmiş bir <b>imzaya</b> göre gruplanır. Her grup; oluşma sayısını, etkilenen oturumları, örnek bir yığın izini ve kayda atlama bağlantısını gösterir. Kutuyu temiz tutmak için grupları çözüldü ya da yok sayıldı olarak işaretleyin.</P>,
      },
      {
        q: 'Uyarılar, önceden haberdar olun',
        tags: ['uyarılar'],
        a: (
          <>
            <P><b>Uyarılar</b> altında dört metrik üzerinde eşik kuralları oluşturun: yüksek hayal kırıklığı oturumları, hata oluşumları, öfke tıklaması olan oturumlar ve yeni hata türleri. Her kuralın kayan bir penceresi ve bekleme süresi vardır; böylece gereksiz bildirim yağmuruna tutulmazsınız.</P>
            <P>Bir kural tetiklendiğinde uygulama içindeki zile düşer ve yapılandırıldıysa Slack ya da Microsoft Teams’e bildirim gider. Bir kuralı hemen denemek için <b>Şimdi değerlendir</b>’i kullanın.</P>
          </>
        ),
      },
      {
        q: 'Canlı. Ortak gezinme, kontrol ve sohbet',
        tags: ['canlı', 'ortak gezinme', 'uzaktan kontrol'],
        a: (
          <>
            <P><b>Canlı</b> ekranı, SDK kurulu sitelerinizde o anda aktif olan kullanıcıları gösterir. Karşı tarafta tarayıcı eklentisi gerekmez. Bir ziyaretçi seçip ekranını gerçek zamanlı izleyin, sohbet edin ve neler olduğunu inceleyin.</P>
            <KV
              cols={['Yetenek', 'Nasıl çalışır']}
              rows={[
                [<b>Birlikte izleme</b>, 'Ziyaretçinin ekranı siz izlerken canlı akar. Video değil, yeniden kurulan DOM.'],
                [<b>Kontrolü devralma</b>, <><b>Kontrol iste</b>’ye tıklayın; ziyaretçi bir onay kutusu görür ve kabul etmelidir. Onay verilince sayfasını siz sürebilirsiniz (imleç, tıklama, kaydırma ve yazma), bir formu doldurmak ya da takıldığı yerden kurtarmak için. Ziyaretçi onayı istediği an geri alabilir.</>],
                [<b>Sohbet</b>, 'Sayfasında çift yönlü bir sohbet aracı belirir.'],
                [<b>Canlı DevTools</b>, 'Ekranın altında, izlemeye başladığınızdan beri olan biteni gösteren gerçek zamanlı bir Ağ / Konsol / Hata / Perf paneli.'],
              ]}
            />
            <P>Uzaktan kontrol komutları yalnızca ziyaretçi onay verdikten sonra uygulanır. Onay kuralı sunucu tarafında zorunlu tutulur.</P>
          </>
        ),
      },
      {
        q: 'Test modu. Test ettiğiniz sayfadan hata bildirin',
        tags: ['test', 'hata bildirimi', 'qa', 'plan', 'koltuk'],
        a: (
          <>
            <P>Testçiler, SDK çalışan her sayfada sohbet balonunun yanında bir 🐞 düğmesi görür. Düğme baktıkları ekranın görüntüsünü alır, üzerine işaret koymalarına (kutu, ok, serbest çizim, metin) ve not eklemelerine izin verir, sonra bir talep açar. Kayıt, tarayıcı/işletim sistemi, hayal kırıklığı sinyalleri ve AI’ın önerdiği başlık ve açıklama zaten ekli gelir. URL kopyalamaya ya da “ne yapıyordunuz?” sorularına gerek kalmaz.</P>
            <KV
              cols={['Adım', 'Ne olur']}
              rows={[
                ['1 · Yakala', 'Düğme o anki ekranı alır (ReplayOps aracı kendini gizler, görüntüye girmez).'],
                ['2 · İşaretle', 'Ekran görüntüsünün üzerine çizin ve neyin yanlış gittiğini yazın.'],
                ['3 · Gönder', 'Formu önceden doldurulmuş bir ReplayOps penceresi açılır; testçi gözden geçirip gönderir.'],
                ['4 · Önceliklendir', 'Talep, oturumu ekli hâlde Talepler’e düşer. Tam o ana ait kayda bir tık uzaklıkta.'],
              ]}
            />
            <P><b>Kimler kullanabilir.</b> Yalnızca <b>Admin → Kullanıcı Yönetimi</b>’nde <b>Testçi</b> işaretli üyeler. Denetim sunucu tarafında yapılır, dolayısıyla sıradan bir ziyaretçi düğmeye dokunsa da bir şey olmaz.</P>
            <P><b>Bu bir plan özelliğidir.</b> Her plan test modunun dahil olup olmadığını ve kaç üyenin bu işareti taşıyabileceğini belirler. Kullanıcı Yönetimi kullanılan sayıyı gösterir ve planın testçi hakkı bitince yeni bir yetki vermeyi reddeder; geri alma asla engellenmez, yani bir düşürme sizi kilitli bırakmaz. Bildirim anında da plan denetlenir. İşaretler düşürmede korunur ama plan yeniden kapsayana kadar çalışmaz. Planınızı <b>Admin → Faturalama</b> altında görün.</P>
          </>
        ),
      },
      {
        q: 'AI Özetleri',
        tags: ['ai', 'özet'],
        a: <P><b>AI Özetleri</b>, bir oturumda ne olduğunun sade dille bir özetini üretir. API anahtarı tanımlıysa Claude’u, değilse belirlenimci bir sezgisel yöntemi kullanır. Böylece dışarı hiç çağrı yapmayan, ağdan yalıtılmış kurulumlarda da çalışır.</P>,
      },
      {
        q: 'MCP: bir asistanı ReplayOps’a bağlamak',
        tags: ['mcp', 'claude', 'chatgpt', 'asistan', 'api belirteci'],
        a: (
          <>
            <P>MCP ile bağlanan bir asistan, "bu müşteri ödeme adımında neden vazgeçti?" sorusunu doğrudan kayıtlardan yanıtlayabilir. Uç, bu kurulumdaki <code>/mcp</code> adresidir ve Streamable HTTP konuşur.</P>
            <P><b>Bir bağlantı projeye değil, şirkete aittir.</b> Sunucu belirteci şirketi ve bir rolü taşır; hiçbir araç kendisine verilmesi gereken bir proje almaz, <code>projectId</code> yalnızca isteğe bağlı bir filtredir. Yani tek bir belirteç, görebildiğiniz bütün projelere ulaşır. <b>Yönetim → API ve MCP erişimi</b> altından oluşturun; bir asistanın erişimini de aynı sayfadan geri alın.</P>
            <P><b>Tavan roldür.</b> VIEWER belirteciyle bağlanan bir asistan her şeyi okur, hiçbir şeyi değiştirmez. Yazma işlemleri araç içinde gerekçesiyle reddedilir; böylece asistan çalışıyormuş gibi yapmak yerine nedenini size söyler.</P>
            <P><b>Başlık ayarlayabilen istemciler</b> (Claude Desktop, Cursor ve benzerleri) belirteci doğrudan kullanır; API Anahtarları sayfasındaki MCP sekmesi hazır yapılandırmayı yazdırır. <b>Barındırılan asistanlar, siz verseniz bile bir belirteç taşımaz.</b> ChatGPT, Slack ve Microsoft 365 için bu kurulumun bir OAuth yetkilendirme sunucusu gibi davranması gerekir. Bu varsayılan olarak kapalıdır; <code>MCP_OAUTH_ENABLED=true</code> yapıp yeniden dağıtın, ardından istemci kendini kaydeder ve kimse bir şey yapıştırmaz.</P>
          </>
        ),
      },
      {
        q: 'Sor: verinizi kendi cümlelerinizle sorgulamak',
        tags: ['sor', 'ai', 'sohbet', 'mcp'],
        a: (
          <>
            <P><b>Sor</b>, kayıtlarınızla ilgili soruları yanıtlar. "ayse@example.com son bir ayda kaç kez hata aldı?" "Bu hafta en çok kim zorlandı?" MCP sunucumuzun sunduğu aynı okuma araçlarını çalıştırır ve sonucu size okur. <b>Yönetim → AI ve Modeller</b> altında bir sağlayıcı gerekir; yoksa sayfa tahmin etmek yerine bunu söyler.</P>
            <P>Bilmeye değer üç şey var. Yalnızca <b>kendi şirketinizin</b> verisine erişir, çünkü hiçbir araç parametre olarak şirket almaz. Tavan sizin rolünüzdür: Viewer her şeyi sorabilir, hiçbir şeyi değiştiremez; talep açmak gerekçesiyle reddedilir. Ve her yanıt, gerçekten çalıştırdığı araçları listeler; böylece ölçülmüş bir sayı ile uydurulmuş bir sayı birbirine benzemez. Liste boşsa yanıtı doğrulanmamış sayın.</P>
            <P>Yanıtlar yine de hatalı olabilir. Yanıtın altındaki araçlara bakın ve adı geçen oturumları açın.</P>
          </>
        ),
      },
      {
        q: 'Analitik: Destek, Kullanıcı Davranışı, Huni ve Isı Haritaları',
        tags: ['analitik', 'huni', 'ısı haritası', 'zorlanan kullanıcılar'],
        a: (
          <>
            <P><b>Destek Analitiği</b>; talep hacmini, çözüm süresini ve atanan kişiye göre iş yükünü kapsar.</P>
            <P><b>Kullanıcı Davranışı</b>; hayal kırıklığı puanı eğilimini, <b>Hayal kırıklığı dağılımını</b> (oturumların puan aralıklarına nasıl yayıldığını), Tıklama Anomalilerini, En Çok Terk Edilen Noktaları ve <b>En Çok Zorlanan Kullanıcılar</b>’ı gösterir. Tanımlı ziyaretçiler en yüksek hayal kırıklıklarına göre, oturum sayısı, öfke tıklaması ve hesapla (e-posta alan adı) birlikte sıralanır.</P>
            <P><b>Dönüşüm Hunisi</b> adımdan adıma kaybı ölçer (özel olaylara ya da sayfa yollarına göre). <b>Isı Haritaları</b> sayfa başına tıklama yoğunluğunu ve bir kaydırma haritasını gösterir; sayfalar varsayılan olarak kanonik rotalara (örneğin <C>/product/:id</C>) gruplanır ve sıcak bir alana tıklamak arkasındaki oturumları listeler.</P>
          </>
        ),
      },
      {
        q: 'Bir oturumu incelemek, DevTools panelleri',
        tags: ['devtools', 'ağ', 'konsol', 'hatalar', 'performans', 'graphql', 'payload'],
        a: (
          <>
            <P>Herhangi bir oturum kaydını açtığınızda oynatıcının yanında tarayıcıdaki gibi DevTools sekmeleri bulursunuz:</P>
            <KV
              cols={['Sekme', 'Gösterdiği']}
              rows={[
                [<b>Konsol</b>, 'Yakalanan console.{log,info,warn,error} çıktısı.'],
                [<b>Ağ</b>, <>Metot, durum ve süresiyle her fetch/XHR. Bir satırı açınca <b>istek/yanıt gövdesi + başlıklar</b> ve varsa <b>GraphQL</b> işlemi görünür. Gövdeler boyutla sınırlıdır, hassas JSON anahtarları maskelenir, auth/cookie başlıkları gizlenir.</>],
                [<b>Hatalar</b>, <>Yığın izleriyle JS/konsol hataları. <b>Sourcemap ile çöz</b>, küçültülmüş bir yığını özgün <C>function @ src/dosya:satır</C> hâline getirir (bkz. Sourcemap’ler).</>],
                [<b>Perf</b>, 'Oturum boyunca örneklenen çalışma zamanı sağlığı. FPS, DOM düğüm sayısı ve JS belleği.'],
                [<b>İlişkilendir</b>, 'Bu oturum için backend APM izine / harici kayıt aracına doğrudan bağlantılar.'],
              ]}
            />
            <P>Payload ve performans verisi SDK tarafından yakalanır, dolayısıyla güncel bir SDK ile kaydedilen oturumlarda görünür; eski kayıtlarda yine üst veriler yer alır.</P>
          </>
        ),
      },
      {
        q: 'Android çökmeleri. Karartılmış bir derlemeden okunur yığın',
        tags: ['android', 'çökme', 'proguard', 'r8', 'mapping', 'sembolleştirme'],
        a: (
          <>
            <P>Bir Android sürüm derlemesi R8 ile karartılır, bu yüzden çökme <C>at a.c.run(SourceFile:17)</C> gibi okunur. İşe yaramaz ve birbirinden bağımsız hatalarda bile aynı görünür. Derlemenizin zaten ürettiği mapping dosyasını yükleyin, yığın kendi adlarınızla geri gelsin.</P>
            <Steps items={[
              <>Bir sürüm derlemesi alın; mapping <C>app/build/outputs/mapping/release/mapping.txt</C> yolunda oluşur.</>,
              <><b>Admin → Sourcemap’ler</b>’i açın, uygulamanızın bildirdiği sürüm numarasını girin ve dosyayı yükleyin. Adından tanınır, seçecek bir şey yoktur.</>,
              <>Çökmeyi <b>Hatalar</b> ekranında açın ve yığını çözün.</>,
            ]} />
            <P>ProGuard kurallarınızda <C>-keepattributes SourceFile,LineNumberTable</C> satırını koruyun, yoksa çözülmüş yığında satır numarası olmaz. Framework’e ait çerçeveler olduğu gibi geçer ve mapping’in tanımadığı bir sınıfa uydurma bir ad verilmez, dokunulmaz.</P>
            <P><b>iOS</b> için hiçbir yükleme gerekmez: çökme çerçevesi karmaşıklaştırılmış Swift sembolünü taşır ve yığını çözmek <C>$s5MyApp14CartControllerC8checkoutyyF</C> ifadesini <C>MyApp.CartController.checkout()</C> hâline getirir. Çökmenin bir closure içinde olup olmadığı dahil. Dosya ve satır numaraları ile sembol tablosu çıkarılmış App Store derlemeleri bir dSYM yüklemesi gerektirir; bu henüz desteklenmiyor.</P>
          </>
        ),
      },
      {
        q: 'Sourcemap’ler, okunabilir hata yığınları',
        tags: ['sourcemap', 'hatalar', 'yığın izi', 'minified'],
        a: (
          <>
            <P>Üretimdeki JS küçültülmüştür, bu yüzden ham yığınlar <C>main.9f3ac1.js:1:284713</C> gibi okunamaz yerleri gösterir. Derlemenizin sourcemap’lerini yükleyin, ReplayOps yığınları özgün kaynağınıza çözsün.</P>
            <Steps items={[
              <>Sourcemap açıkken derleyin (Vite: <C>build.sourcemap: true</C>). Paketinizin yanında <C>.map</C> dosyaları oluşur.</>,
              <><b>Admin → Sourcemap’ler</b>’i açın, <b>projeyi</b> seçin, <b>sürüm numarasını</b> girin (SDK’nın bildirdiğiyle birebir aynı olmalı) ve <C>.map</C> dosyalarını yükleyin.</>,
              <>O sürümdeki bir oturumda <b>Hatalar → Sourcemap ile çöz</b> deyip özgün <C>function @ kaynak:satır</C> bilgisini görün.</>,
            ]} />
            <P>Haritalar proje + sürüm bazında (gzip’lenmiş) saklanır ve yalnızca sunucu tarafında sembolleştirme için kullanılır. Tarayıcıya hiç sunulmaz.</P>
          </>
        ),
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Gizlilik ve maskeleme',
    blurb: 'Neyin kaydedileceğine siz karar verin. Maskeleme tarayıcıda, hiçbir şey gönderilmeden önce yapılır.',
    icon: <VisibilityOffIcon />,
    articles: [
      {
        q: 'Maskeleme nasıl çalışır',
        tags: ['gizlilik', 'maskeleme', 'gdpr', 'kvkk'],
        a: (
          <>
            <P>ReplayOps video değil DOM kaydeder. Bu yüzden maskeleme, sonradan piksellerin üzerine çekilmiş bir bulanıklık değildir. Maskelenen metin, parti gönderilmeden önce <b>tarayıcıda, yakalama sırasında</b> değiştirilir. Gerçek değer collector’a, oturum deposuna ya da bir kayda hiç ulaşmaz: sonradan sızacak bir şey de, bulanıklığı kaldırılacak bir şey de yoktur.</P>
            <KV
              cols={['Düzey', 'Ne olur', 'Nasıl işaretlenir']}
              rows={[
                [<b>Maskele</b>, <>Öğe ve tüm alt ağacı yine kaydedilir ama metnin her karakteri değiştirilir (sayfa metni için <C>***</C>, girdi değerleri için <C>•••</C>). Yerleşim ve etkileşimler bozulmaz.</>, <><C>data-mask</C>, <C>data-private</C> ya da <C>maskSelectors</C></>],
                [<b>Yok say</b>, 'Öğe kayıttan tamamen çıkarılır ve aynı boyutta boş bir yer tutucu kutu olarak izlenir.', <><C>data-ignore</C> ya da <C>ignoreSelectors</C></>],
                [<b>Her zaman açık</b>, 'Parola, kart, CVV, kimlik numarası ve benzeri alanlar siz işaretleseniz de işaretlemeseniz de maskelenir. Bu katman kapatılamaz.', 'otomatik'],
              ]}
            />
            <P>Pratik kural: sayfanın şeklini görmeye devam etmek istiyorsanız <b>maskeleyin</b>, öğenin hiç yakalanmaması gerekiyorsa <b>yok sayın</b> (bir belge görüntüleyici, müşteri fotoğrafı, üçüncü taraf ödeme iframe’i).</P>
          </>
        ),
      },
      {
        q: 'Bir bölgeyi maskeleme. İki etiket arasındaki her şey',
        tags: ['gizlilik', 'maskeleme', 'data-mask', 'data-ignore'],
        a: (
          <>
            <>
              <P>Her iki öznitelik de tüm alt ağaca kalıtılır; bir bölgeyi bir kez sarmalarsınız, içindeki her şey kapsanır. Öğe öğe işaretlemeye gerek yoktur.</P>
            </>
            <Code>{`<!-- Recorded, but all text inside is masked -->
<section data-mask>
  <h3>Melih Sakarya</h3>
  <p>TR33 0006 1005 1978 6457 8413 26</p>
  <table>…</table>
</section>

<!-- Not recorded at all -->
<div data-ignore>
  <iframe src="https://bank.example/widget"></iframe>
  <img src="/uploads/id-card.jpg" />
</div>`}</Code>
            <P><C>data-private</C>, <C>data-mask</C> için bir diğer addır. Biçimlendirmeye dokunamadığınızda (üçüncü taraf bir araç, bir tasarım sistemi bileşeni) bunun yerine init sırasında CSS seçicileri verin; özniteliklerle birebir aynı davranırlar:</P>
            <Code>{`ReplayOps.init({
  projectId: '…', apiKey: '…', endpoint: '…',
  maskSelectors:   ['.invoice-total', '#patient-notes'],
  ignoreSelectors: ['.stripe-card-frame', 'iframe[src*="bank."]'],
});`}</Code>
            <P>Çalışma zamanında <C>Session.mask(selector)</C> ve <C>Session.ignore(selector)</C>, bir sonraki <C>start()</C> ile geçerli olacak kurallar ekler. Kaydedilmiş olaylar asla yeniden yazılmaz. Maskeleme yakalama anına ait bir karardır.</P>
          </>
        ),
      },
      {
        q: 'Örnekler: bir div’i, bir tablo sütununu, bir listeyi, bir aracı maskeleme',
        tags: ['gizlilik', 'maskeleme', 'örnek', 'div', 'tablo', 'seçici'],
        a: (
          <>
            <P><b>Bir div’in tamamı ve içindeki her şey.</b> Kapsayıcıyı bir kez işaretleyin. İç bileşenler ve sonradan JavaScript ile eklenen metinler de kapsanır.</P>
            <Code>{`<div data-mask class="customer-card">
  <h4>Ayşe Yılmaz</h4>
  <span>ayse@example.com</span>
  <span>+90 532 000 00 00</span>
</div>`}</Code>
            <P><b>Bir tablonun yalnızca bazı sütunları.</b> Özniteliği tabloya değil hücrelere koyun. Sipariş numarası ve tutar destek için okunur kalsın.</P>
            <Code>{`<tr>
  <td>#1042</td>                     <!-- visible -->
  <td data-mask>Ayşe Yılmaz</td>     <!-- masked -->
  <td data-mask>ayse@example.com</td>
  <td>₺1.249,00</td>                 <!-- visible -->
</tr>`}</Code>
            <P>Tüm bir sütun için seçici daha az tekrarlıdır (<C>nth-child</C> 1’den saymaya başlar):</P>
            <Code>{`maskSelectors: ['.orders td:nth-child(2)', '.orders td:nth-child(3)']`}</Code>
            <P><b>Veriden üretilen bir liste.</b> Öznitelik tekrar eden öğeye konur, böylece liste ne kadar uzarsa uzasın her satır kapsanır.</P>
            <Code>{`{patients.map((p) => (
  <li key={p.id} data-mask>{p.fullName}, {p.diagnosis}</li>
))}`}</Code>
            <P><b>Etiketi koruyun, değeri maskeleyin:</b> kayıt, kullanıcının hangi alanda zorlandığını değeri göstermeden yine anlatır.</P>
            <Code>{`<div class="field">
  <span class="label">IBAN</span>
  <span class="value" data-mask>TR33 0006 …</span>
</div>`}</Code>
            <P><b>Size ait olmayan biçimlendirme</b> (bir tedarikçi aracı). Bunun yerine init sırasında seçiciyle hedefleyin:</P>
            <Code>{`maskSelectors: [
  '#intercom-container',
  '.pdf-viewer .page',
  '[data-testid="ssn-field"]',
  'section[aria-label="Medical history"]',
]`}</Code>
            <P>Şeklin kendisi bile hassassa ya da öğe ağırsa ve kayıtta değersizse <b>tamamen çıkarın</b>:</P>
            <Code>{`<div data-ignore>
  <img src="/uploads/id-card.jpg" />
  <canvas id="signature-pad"></canvas>
  <iframe src="https://bank.example/3d-secure"></iframe>
</div>`}</Code>
            <P><b>Çalışma zamanında</b>, hassaslık duruma bağlıysa:</P>
            <Code>{`if (user.role === 'patient') {
  ReplayOps.mask('.records-panel');
  ReplayOps.stop(); ReplayOps.start();   // rules apply on the next start
}`}</Code>
            <P>Maskeleme tüm alt ağaç için her zaman kazanır. Maskelenmiş bir ebeveynin çocuğunu maskesiz yapmanın yolu yoktur. Bu yüzden kurguyu “her şeyi maskele, istisna aç” değil, “hassas kapsayıcıları maskele” diye yapın.</P>
          </>
        ),
      },
      {
        q: 'Form girdileri, ağ gövdeleri ve dağıtım politikası',
        tags: ['gizlilik', 'maskeleme', 'girdiler', 'ağ'],
        a: (
          <>
            <P><C>maskInputs</C> (varsayılan açık) her input, textarea ve select değerini maskeler. Kapatsanız bile; türü <C>password</C>/<C>email</C>/<C>tel</C>/<C>number</C>/<C>hidden</C> olan, <C>autocomplete</C> değeri kart ya da parola belirten veya adı/id’si/aria-label’ı <C>password</C>, <C>card</C>, <C>cvv</C>, <C>iban</C>, <C>ssn</C>, <C>tckn</C>, <C>secret</C>, <C>token</C>, <C>pin</C> ile eşleşen bir alan yine maskelenir.</P>
            <P>Yakalanan ağ gövdeleri 8&nbsp;KB ile sınırlıdır; hassas görünen JSON anahtarları hangi derinlikte olursa olsun <C>***</C> ile değiştirilir ve <C>authorization</C>/<C>cookie</C>/<C>x-api-key</C> türü başlıklar gizlenir. Yalnızca metot, URL, durum ve süreyi tutmak için <C>recordNetworkBodies: false</C> verin.</P>
            <P><b>Admin → Güvenlik</b> dağıtım genelinde bir maskeleme politikası taşır. SDK bunu init sırasında alır ve politika, tek tek sitelerin yapılandırmasını <b>geçersiz kılar</b>; böylece kimsenin frontend’ini yeniden dağıtmadan maskelemeyi her yerde zorunlu kılabilirsiniz. Collector’a ulaşılamazsa SDK gizliliği koruyan varsayılanda kalır, maskeleme açık.</P>
          </>
        ),
      },
      {
        q: 'Onay, saklama ve unutulma hakkı (GDPR / KVKK)',
        tags: ['gizlilik', 'gdpr', 'kvkk', 'onay', 'saklama'],
        a: (
          <>
            <P><C>requireConsent: true</C> verildiğinde SDK beklemede kalır (anlık görüntü yok, parti yok), ta ki <C>Session.consent(true)</C> çağrılana kadar. Kod parçasını çerez bildiriminiz sonuçlanmadan önce yüklemek güvenlidir. <C>Session.optOut()</C> o tarayıcı için kaydı kalıcı olarak durdurur.</P>
            <P>Her projenin bir <b>saklama penceresi</b> vardır (varsayılan 90 gün); saklama işçisi süresi dolan oturumları ve parçalarını otomatik siler. Bir veri sahibi talebinde, organizasyon yöneticisi tek bir denetlenen işlemle bir uygulama kullanıcısının oturumlarını üst veride, nesne depolamada ve arama dizininde silebilir.</P>
          </>
        ),
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Entegrasyonlar',
    blurb: 'Bildirim araçlarını, iş takip sistemlerini, APM’i ve diğer kayıt araçlarını bağlayın.',
    icon: <ExtensionIcon />,
    articles: [
      {
        q: 'Slack / Teams / Jira / GitHub … bağlayın',
        tags: ['entegrasyon'],
        a: (
          <>
            <P><b>Admin → Entegrasyon Merkezi</b>’ni açın. Her sağlayıcının kendi kimlik alanları vardır; kaydetmeden önce (varsa) <b>Test</b>’i kullanın.</P>
            <Steps items={[
              <><b>Uyarı ve sohbet</b> (Slack, Teams, Discord, Google Chat, e-posta): bir webhook URL’si yapıştırın, uyarılar bunu kullanır.</>,
              <><b>Olay ve nöbet</b> (PagerDuty, Opsgenie): o an nöbette kim varsa ona haber verir. PagerDuty aynı uyarının tekrarlarını her tetiklemede ayrı çağrı yapmak yerine tek bir olayda birleştirir.</>,
              <><b>İş takibi</b> (Jira, GitHub, Linear, Trello, Asana, ClickUp, Azure DevOps, GitLab) ve <b>müşteri desteği</b> (Zendesk, Freshdesk, ServiceNow, Intercom, Help Scout, Salesforce): bir oturumdan talep açın; kayda geri bağlantı verir, oturum bilgilerini ve <b>hayal kırıklığı puanından eşlenen bir önceliği</b> taşır. Yardım masaları ziyaretçiyi talep sahibi olarak ekler. <b>Test</b>, bir şey oluşturmadan önce kimlik bilgilerinizi doğrular.</>,
              <><b>APM ve izleme</b> (Datadog, New Relic, AppDynamics, Dynatrace, Honeycomb, OpenTelemetry) ve <b>günlükler</b> (Splunk, Kibana, Sumo Logic, Grafana): bir oturumu arkasındaki izlere ve günlüklere doğrudan bağlayın.</>,
              <><b>Ürün analitiği</b> (GA, Amplitude, Heap, Pendo, PostHog, Segment, Mixpanel), <b>hata izleme</b> (Sentry, Rollbar, Bugsnag, Crashlytics), <b>bayraklar</b> (LaunchDarkly, Optimizely), <b>müşterinin sesi</b> (Qualtrics, Delighted) ve <b>diğer kayıt araçları</b> (FullStory, LogRocket, Hotjar, Clarity).</>,
            ]} />
            <P>Her kart dört mekanizmadan hangisini kullandığını söyler. <b>API</b> (yazdığımız bir istemci, talep kimliği döner), <b>webhook</b> (yapıştırdığınız URL’ye JSON POST ederiz), <b>yalnızca bağlantı</b> (bir URL şablonu; ReplayOps’tan hiçbir şey çıkmaz ve saklanacak bir kimlik bilgisi yoktur) ya da <b>e-posta ile</b> (masanızın giriş adresine posta atarak talep açarız). İstediğiniz araç listede yoksa ama webhook kabul ediyor ya da aranabilir bir URL’si varsa, en yakın webhook / yalnızca-bağlantı kartını kendi URL’nizle kullanın.</P>
          </>
        ),
      },
      {
        q: 'Diğer araçlara doğrudan bağlantı nasıl çalışır',
        tags: ['entegrasyon', 'apm', 'harici kayıt'],
        a: <P>ReplayOps verilerinizi başka sistemlerden kopyalamaz. Oturumda yakalanan tanımlayıcılardan (kullanıcı kimliği, iz kimliği, zaman damgası) her sağlayıcının URL şablonunu kullanarak bir bağlantı kurar. Bu, kurulumu sağlayıcı başına tek bir kimlik/URL’de tutar ve verinin kaynak araçta kalmasını sağlar.</P>,
      },
    ],
  },
  {
    id: 'onprem',
    title: 'Kendi sunucunuzda ve yapılandırma',
    blurb: 'ReplayOps’u kendi altyapınızda, kendi bildiğiniz gibi çalıştırın.',
    icon: <ShieldIcon />,
    articles: [
      {
        q: 'ReplayOps’u kendi altyapınızda çalıştırın',
        tags: ['on-prem', 'ağdan yalıtılmış', 'self-hosted'],
        a: (
          <>
            <P>ReplayOps, AWS bağımlılığı olmadan tamamen kendi sunucunuzda çalışabilir. Yalnızca Postgres ve Redis gereklidir. Tek komutluk bir yığın için <C>docker-compose.airgapped.yml</C> dosyasını kullanın (bkz. <C>docs/AIRGAPPED.md</C>).</P>
            <P>Arka uçlarınızı <b>Admin → Yapılandırma</b>’dan seçin: oturum depolaması S3, MinIO, Ceph ya da yerel disk; ingest kuyruğu SQS ya da Redis; arama OpenSearch ya da Postgres yedeği.</P>
          </>
        ),
      },
      {
        q: 'Oturum depolaması: S3, MinIO, Ceph ya da yerel disk',
        tags: ['depolama', 's3', 'minio', 'ceph', 'yerel'],
        a: (
          <>
            <P><b>Yapılandırma → Oturum depolaması</b> altından bir <b>sürücü</b> seçin:</P>
            <KV
              cols={['Sürücü', 'Ne zaman kullanılır', 'Önemli alanlar']}
              rows={[
                ['Yerel disk', 'Tek düğümlü / ağdan yalıtılmış kurulumlar.', <>Yalnızca yazılabilir bir <b>yol</b>.</>],
                ['S3 (AWS)', 'AWS üzerinde.', <>Bucket + Bölge; <b>Endpoint</b>’i boş bırakın.</>],
                ['S3 (MinIO / Ceph)', 'Kendi nesne deponuz.', <><b>Sağlayıcı ön ayarını</b> seçin, ağ geçidi <b>Endpoint</b>’ini ve kimlik bilgilerini girin.</>],
              ]}
            />
            <P><b>Path-style adresleme</b> varsayılan olarak <C>Auto</C>’dur: gerçek AWS için virtual-host, bir endpoint verildiğinde (MinIO/Ceph) path-style. Yalnızca ağ geçidiniz tersini gerektiriyorsa değiştirin.</P>
          </>
        ),
      },
      {
        q: 'Kuyruk ve arama arka uçları',
        tags: ['kuyruk', 'redis', 'sqs', 'opensearch', 'arama'],
        a: (
          <>
            <P><b>Ingest kuyruğu</b>: <b>Redis</b> (kendi sunucunuzda, yalnızca bir URL) ya da <b>SQS</b> (bir kuyruk URL’si + AWS kimlik bilgileri) seçin.</P>
            <P><b>Arama</b>: hızlı tam metin araması ve huniler için <b>OpenSearch</b>’ü (düğüm URL’si + dizin) açın ya da kapatıp Postgres’e düşün. Kayıt, tekrar oynatma ve arama çalışmaya devam eder, yalnızca sıralama daha yalındır.</P>
          </>
        ),
      },
      {
        q: 'Kimlik bilgilerini girmek (ve sırların nasıl saklandığı)',
        tags: ['yapılandırma', 'sırlar', 'kimlik bilgileri'],
        a: (
          <>
            <P>Erişim anahtarlarını, sırları ve bağlantı URL’lerini doğrudan <b>Yapılandırma</b>’ya girin. Sırlar <b>yalnızca yazılır</b>: API saklanan bir sırrı tarayıcıya asla geri göndermez, alan yalnızca bir değer olup olmadığını gösterir ve boş bırakmak <b>mevcut değeri korur</b>. Denetim günlüğüne yalnızca değişen anahtarların <em>adları</em> yazılır, değerleri asla.</P>
          </>
        ),
      },
      {
        q: 'Bağlantıları test etmek ve değişiklikleri uygulamak',
        tags: ['yapılandırma', 'bağlantı testi', 'yeniden başlatma'],
        a: (
          <>
            <P><b>Yapılandırma</b>’daki her bölümde, girdiğiniz değerlerle arka ucu yoklayan bir <b>Bağlantıyı test et</b> düğmesi vardır. Henüz kaydetmeden. Boş bırakılan bir sır çalışandan okunur, böylece yeniden yazmadan test edebilirsiniz.</P>
            <P>Ayarlar veritabanına kaydedilir ve ortam değişkenlerinin üzerine katmanlanır. Depolama/kuyruk/arama süreç düzeyinde sürücüler olduğundan, kaydedilen bir değişiklik <b>yeniden başlatma gerekli</b> uyarısı gösterir ve API, collector ile işçileri yeniden başlattığınızda etkili olur. Ayarlar veritabanından yönetilmeye başlandığında kaydet düğmesi <b>Yapılandırmayı güncelle</b> yazar.</P>
          </>
        ),
      },
      {
        q: 'Güvenlik ve veri yerleşimi',
        tags: ['güvenlik', 'denetim'],
        a: <P>Tek kiracılı dağıtımlar tek bir organizasyon oluşturur ve ilk owner var olduğunda açık kaydı kapatır. <b>Admin → Güvenlik</b>; dağıtımın duruşunu, etkin API anahtarlarını ve yalnızca eklenebilir bir denetim günlüğünü gösterir. Ağdan yalıtılmış modda hiçbir veri ağınızdan çıkmaz. Yerel sezgisel yönteme düşen AI Özetleri dahil.</P>,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Sorun giderme',
    blurb: 'Sık karşılaşılan hatalar ve nasıl giderilecekleri.',
    icon: <HealingIcon />,
    articles: [
      {
        q: 'Depolama bağlantı testi başarısız oluyor',
        tags: ['sorun giderme', 's3', 'bağlantı testi', '403', 'hata'],
        a: (
          <>
            <P>Test, nesne deposunun döndürdüğü tam nedeni bildirir. Mesajı eşleştirin:</P>
            <KV
              cols={['Mesaj', 'Olası neden', 'Çözüm']}
              rows={[
                [<>Erişim reddedildi (HTTP 403)</>, 'Erişim anahtarı/sırrı bucket’ı göremiyor ya da bucket politikası izin vermiyor.', 'Geçerli kimlik bilgilerini yeniden girin; anahtarda s3:ListBucket / HeadBucket yetkisi olduğunu doğrulayın.'],
                [<>Bucket bulunamadı (HTTP 404)</>, 'Bucket adı ya da Bölge yanlış.', 'Bucket adını birebir ve Bölgenin bucket’ın bulunduğu yer olduğunu kontrol edin.'],
                [<>Yanlış bölge…</>, 'Bucket, yapılandırılandan farklı bir bölgede.', 'Bölgeyi mesajda adı geçen bölge olarak ayarlayın.'],
                [<>Yanlışlıkla gerçek AWS’e gitmek</>, 'MinIO/Ceph/LocalStack kullanmak isterken Endpoint boş kalmış.', <>Ağ geçidi URL’sini <b>Endpoint</b> alanına yazın (örneğin LocalStack için <C>http://localhost:4566</C>).</>],
              ]}
            />
          </>
        ),
      },
      {
        q: 'Kaydedilen değişiklik etkili olmuyor',
        tags: ['sorun giderme', 'yeniden başlatma', 'yapılandırma'],
        a: <P>Depolama, kuyruk ve arama süreç açılışında seçilir; bu yüzden kaydedilen bir değişiklik servisleri (API, collector, işçiler) yeniden başlatana kadar beklemede kalır. Çalışan süreçler kaydedilen ayarlarla eşleştiğinde <b>yeniden başlatma gerekli</b> uyarısı kaybolur.</P>,
      },
      {
        q: 'Hiç oturum ya da veri görünmüyor',
        tags: ['sorun giderme', 'veri yok', 'sdk'],
        a: (
          <Steps items={[
            <>SDK kod parçasının sayfada olduğunu ve <C>projectId</C> / <C>apiKey</C> değerlerinin baktığınız projeyle eşleştiğini doğrulayın.</>,
            <>Tarayıcı konsolunda collector hatası olup olmadığına ve <C>endpoint</C> adresine erişilebildiğine bakın (CORS / ağ).</>,
            <>Doğru <b>projeye</b> ve zaman aralığına baktığınızdan emin olun.</>,
            <>Kendi sunucunuzda: collector ve işçilerin çalıştığını, kuyruğun (Redis/SQS) boşaldığını doğrulayın.</>,
          ]} />
        ),
      },
      {
        q: 'Uyarılar tetiklenmiyor',
        tags: ['sorun giderme', 'uyarılar'],
        a: <P>Kuralın <b>penceresini</b> ve <b>bekleme süresini</b> kontrol edin. Yakın zamanda bir tetikleme olduysa bekleme süresi dolana kadar bir sonraki bastırılır. Denetimi zorlamak için <b>Şimdi değerlendir</b>’i kullanın ve dışarıdan bildirim bekliyorsanız Entegrasyonlar’da Slack/Teams webhook’unun tanımlı olduğunu doğrulayın.</P>,
      },
    ],
  },
  {
    id: 'faq',
    title: 'SSS',
    blurb: 'Sık sorulan sorulara kısa yanıtlar.',
    icon: <LiveHelpIcon />,
    articles: [
      { q: 'Kullanıcılar nasıl giriş yapar? (Google, parola unuttum)', a: <P>E-posta + parola ile ya da <b>Google ile devam et</b> ile giriş yapılır (Google OAuth tanımlıysa görünür. <C>GOOGLE_CLIENT_ID</C>/<C>GOOGLE_CLIENT_SECRET</C> ayarlayın ve <C>&lt;app&gt;/v1/auth/google/callback</C> adresini yetkili yönlendirme URI’si olarak ekleyin). Parolanızı mı unuttunuz? Giriş ekranındaki <b>Parolamı unuttum</b> ile e-postayla bir saatlik sıfırlama bağlantısı alın (e-posta/SMTP yapılandırması gerekir).</P>, tags: ['kimlik doğrulama', 'google', 'sso', 'parola sıfırlama', 'giriş'] },
      { q: 'Üçüncü taraf analitik neden hata olarak görünmüyor?', a: <P>Durumu 0 olan bir çapraz köken isteği (örneğin opak ya da reklam engelleyiciye takılmış bir Google Analytics beacon’ı) ağ hatası ya da sürtünme sayılmaz. Yalnızca birinci taraf başarısızlıkları ve gerçek 5xx sayılır. Bu, analitik/piksel gürültüsünü hata kutunuzdan ve hayal kırıklığı puanından uzak tutar.</P>, tags: ['hatalar', 'analitik', 'hayal kırıklığı', 'ağ'] },
      { q: 'Ingest API anahtarı bir sır mı?', a: <P>Hayır. Tarayıcının olay gönderebilmesi için istemci kodunuza gömülen, herkese açık ve projeye özgü bir anahtardır. Yalnızca kendi projesi için oturum yayımlayabilir.</P>, tags: ['güvenlik', 'api anahtarı'] },
      { q: 'Ekip arkadaşımı nasıl eklerim?', a: <P><b>Admin → Kullanıcı Yönetimi</b>’ni açın ve <b>Kullanıcı ekle</b>’ye tıklayın (yalnızca owner/admin). Tek kiracılı kurulumlarda kullanıcılar açık kayıt yerine buradan tanımlanır.</P>, tags: ['kullanıcılar'] },
      { q: 'Oturumlar ne kadar saklanır?', a: <P>Proje bazında saklama süresi (varsayılan 90 gün). Daha eski oturumlar saklama işçisi tarafından Postgres’ten, depolamadan ve arama dizininden temizlenir.</P>, tags: ['saklama'] },
      { q: 'OpenSearch şart mı?', a: <P>Hayır. Yapılandırma’dan kapatın; arama ve huniler Postgres’e düşer. Kayıt, tekrar oynatma ve arama çalışmaya devam eder.</P>, tags: ['arama', 'opensearch'] },
      { q: 'Kendi sunucumdaki kurulumda ağımdan dışarı bir şey çıkıyor mu?', a: <P>Ağdan yalıtılmış mod için yapılandırıldığında hayır. Depolama, kuyruk ve arama tamamen yereldir; AI Özetleri de dışarı çağrı yapmak yerine yerleşik sezgisel yönteme düşer.</P>, tags: ['güvenlik', 'ağdan yalıtılmış'] },
      { q: 'Genel aramayı nasıl açarım?', a: <P>Talepler ve oturumlar içinde arama yapmak için her yerde <C>⌘K</C> (macOS) ya da <C>Ctrl K</C> (Windows/Linux) tuşlarına basın.</P>, tags: ['kısayollar', 'arama'] },
    ],
  },
  {
    id: 'glossary',
    title: 'Sözlük',
    blurb: 'Ürün genelinde kullanılan terimler.',
    icon: <MenuBookIcon />,
    articles: [
      {
        q: 'Terimler ve tanımlar',
        tags: ['sözlük', 'tanımlar'],
        a: (
          <KV
            cols={['Terim', 'Tanım']}
            rows={[
              [<b>Öfke tıklaması</b>, 'Küçük bir alanda üç ya da daha fazla hızlı tıklama. Güçlü bir hayal kırıklığı sinyali.'],
              [<b>Ölü tıklama</b>, 'DOM’da gözle görülür hiçbir tepki üretmeyen tıklama.'],
              [<b>Hayal kırıklığı puanı</b>, 'Zorlanma sinyallerini özetleyen, oturum başına 0–100 arası puan.'],
              [<b>İmza</b>, 'Birbirinin aynısı hataları gruplamak için kullanılan normalleştirilmiş parmak izi.'],
              [<b>Terk noktası</b>, 'Yolculukta kullanıcıların kayda değer bir bölümünün vazgeçtiği adım.'],
              [<b>Tek kiracılı</b>, 'Tam olarak bir organizasyonu olan dağıtım; ilk owner’dan sonra açık kayıt kapanır.'],
              [<b>Path-style adresleme</b>, 'MinIO/Ceph’in kullandığı S3 URL biçimi (bucket yolun içinde); AWS virtual-host kullanır.'],
              [<b>Collector</b>, 'SDK olaylarını alan ve işlenmek üzere kuyruğa koyan servis.'],
            ]}
          />
        ),
      },
    ],
  },
];

export function helpGroups(lang: Lang): Group[] {
  return lang === 'tr' ? TR : EN;
}

export type { Article, Group };
